import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useReducer,
  useCallback,
  useMemo,
} from "react";
import { Logo } from "@/components/Logo";
import { ErrorBanner } from "@/components/ErrorBanner";
import { useWebSocket } from "../../context/WebSocketContext";
import type { SystemTask } from "@/components/tasks/SystemTasks";
import { ChatInput } from "./ChatInput";
import { OutboundMessageQueuePanel } from "./OutboundMessageQueuePanel";
import { useOutboundMessageQueue } from "./useOutboundMessageQueue";
import { LoadingIndicator } from "../indicators/LoadingIndicator";
import { motion, AnimatePresence } from "motion/react";
import { sanitizeChatMessageHtml, sanitizeRichHtml } from "@/utils/sanitizeHtml";
import {
  ANI_MINIMAL_ASSISTANT_TURN_BREAK_CLASS,
  CHAT_ASSISTANT_COPY_BUTTON_POSITION_CLASS,
  CHAT_MESSAGE_CONTENT_CLASS,
} from "./chatMessageChrome";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFileCsv,
  faFilePowerpoint,
  faFile,
  faExternalLinkAlt,
  faSparkles,
  faDownload,
  faChevronDown,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { ChatDayBreak } from "./ChatDayBreak";
import {
  shouldShowDayBreak,
  shouldShowPdfIconOnViewAction,
} from "@/utils/chatUtils";
import { PDFAPI } from "@/services/api";
import { getAgenticSystemJobs } from "@/services/api/agentic";
import type { SystemJob } from "@/services/api/agentic/types";
import { apiRequestWithHeaders } from "@/services/api/client";
import type { WebSocketMessage } from "@/services/WebSocketManager";
import axios from "axios";

import WorkerAvatar from "@/components/worker/WorkerAvatar";
import { useAuth } from "@/context/AuthContext";
import { useFloatingAniProfileCompanyId } from "@/hooks/useFloatingAniProfileCompanyId";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { canAttemptWebSocketAuth } from "@/utils/wsAuth";
import { userAvatarSeed, userDisplayName } from "@/utils/userDisplayName";
import { Spinner } from "@/components/ui/spinner";
import { SystemJobTimer } from "./SystemJobTimer";
import { AniDeveloperMenu } from "./AniDeveloperMenu";
import type { FeToolDebugRunner } from "./AniFeToolDebugPanel";
import type { RichContext } from "@/hooks/useRichAniContext";
import type { OsAniContextSnapshot } from "@/components/ui/os-layout/buildOsAniContextSnapshot";
import { ChatEmbeds } from "./embeds/ChatEmbeds";
import { EntityPreviewCardFocusProvider } from "./embeds/EntityPreviewCardFocusContext";
import { parseChatEmbeds, type ChatEmbed } from "./embeds/types";
import { TaskThreadDrawerProvider } from "./embeds/TaskThreadDrawer";
import { ToolStepRow, parseToolStep, type ToolStep } from "./embeds/ToolStepRow";
import { ToolStepSearchEmbedRow } from "./embeds/ToolStepSearchEmbedRow";
import { ToolStepDataTableEmbedRow } from "./embeds/ToolStepDataTableEmbedRow";
import {
  canMergeToolStepWithFollowingDataTableEmbeds,
  canMergeToolStepWithFollowingSearchEmbeds,
  dataTableEmbedsFromSegment,
  searchEmbedsFromSegment,
} from "./embeds/timelineSearchEmbedMerge";
import { ThinkingRow } from "./embeds/ThinkingRow";
import { trimPartialTag, type ThinkingStatus } from "@/services/inAppAgent/turnTimeline";
import { useSmoothText } from "@/hooks/useSmoothText";
import { ChatAssistantContent } from "./ChatAssistantContent";
import { ChatAssistantCopyButton } from "./ChatAssistantCopyButton";
import { assistantMarkdownForCopy } from "./chatAssistantCopy";

export type ChatMessageAction = {
  type: string;
  label: string;
  url?: string;
  metadata?: { filename?: string; mime_type?: string };
};

/** View/download rows after `.filter()`; avoids `url as string` in render paths. */
export type ChatMessageActionWithUrl = ChatMessageAction & {
  url: string;
  label: string;
};

function isChatMessageActionWithUrl(
  action: ChatMessageAction,
): action is ChatMessageActionWithUrl {
  return (
    typeof action.url === "string" &&
    action.url.length > 0 &&
    typeof action.label === "string" &&
    action.label.length > 0
  );
}

/** Wire-format job document from Mongo change streams (subset used by Chat). */
interface WsSystemJobDocument {
  _id?: string;
  id?: string;
  type?: string;
  description?: string;
  status?: string;
  priority?: number;
  dependencies?: string[];
  attempts?: number;
  max_attempts?: number;
  input?: SystemJob["input"];
  output?: SystemJob["output"];
  created_at?: string;
  updated_at?: string;
  completed_at?: string | null;
  conversation_id?: string;
  pod_instance_id?: string;
}

/** System job row shown in Chat (REST list + WS updates + brief “completed” chip). */
export interface ChatSystemJobRow {
  _id?: string;
  id?: string;
  type?: string;
  description?: string;
  status?: string;
  priority?: number;
  dependencies?: string[];
  attempts?: number;
  max_attempts?: number;
  input?: SystemJob["input"];
  output?: SystemJob["output"];
  created_at?: string;
  updated_at?: string;
  completed_at?: string | null;
  conversation_id?: string;
  pod_instance_id?: string;
  completedAt?: number;
}

/** API `SystemJob` may include `conversation_id` on some deployments. */
type SystemJobFromApi = SystemJob & { conversation_id?: string };

function systemJobToChatRow(job: SystemJob): ChatSystemJobRow {
  const j = job as SystemJobFromApi;
  return {
    _id: j._id,
    id: j._id,
    type: j.type,
    description: j.description,
    status: j.status,
    priority: j.priority,
    dependencies: j.dependencies,
    attempts: j.attempts,
    max_attempts: j.max_attempts,
    input: j.input,
    output: j.output,
    created_at: j.created_at,
    updated_at: j.updated_at,
    completed_at: j.completed_at,
    pod_instance_id: j.pod_instance_id,
    conversation_id: j.conversation_id,
  };
}

/** One boundary cast: WS `full_document` matches this subset at runtime. */
function wsDocumentToPartialRow(doc: object): Partial<ChatSystemJobRow> {
  const d = doc as WsSystemJobDocument;
  return {
    _id: d._id,
    id: d.id,
    type: d.type,
    description: d.description,
    status: d.status,
    priority: d.priority,
    dependencies: d.dependencies,
    attempts: d.attempts,
    max_attempts: d.max_attempts,
    input: d.input,
    output: d.output,
    created_at: d.created_at,
    updated_at: d.updated_at,
    completed_at: d.completed_at,
    conversation_id: d.conversation_id,
    pod_instance_id: d.pod_instance_id,
  };
}

const WS_JOB_ROW_MERGE_DEFAULTS: ChatSystemJobRow = {
  type: "unknown",
  description: "",
  status: "unknown",
  priority: 1,
  dependencies: [],
  attempts: 0,
  max_attempts: 3,
  input: {},
  output: {},
};

function mergeWsSystemJobFullDocument(fullDoc: object): ChatSystemJobRow {
  const partial = wsDocumentToPartialRow(fullDoc);
  return {
    ...WS_JOB_ROW_MERGE_DEFAULTS,
    ...partial,
    input: partial.input ?? WS_JOB_ROW_MERGE_DEFAULTS.input,
    output: partial.output ?? WS_JOB_ROW_MERGE_DEFAULTS.output,
    dependencies:
      partial.dependencies ?? WS_JOB_ROW_MERGE_DEFAULTS.dependencies,
  };
}

function wsMessageToPayloadRecord(message: WebSocketMessage): object | null {
  const raw =
    message.payload !== undefined && message.payload !== null
      ? message.payload
      : message;
  if (typeof raw !== "object" || raw === null) return null;
  return raw as object;
}

function readFullDocumentFromPayload(envelope: object): object | null {
  if (!("full_document" in envelope)) return null;
  const rec = envelope as { full_document?: object | null };
  const fd = rec.full_document;
  if (fd == null || typeof fd !== "object") return null;
  return fd;
}

export type ChatMessageAttachmentType =
  | "document"
  | "entity_document"
  | "conversation_document";

export type ChatMessageAttachment = {
  type: ChatMessageAttachmentType;
  document_id: string;
  display_name?: string;
  mime_type?: string;
  size?: number;
};

export type ChatMessageMetadata = {
  embeds?: ChatEmbed[];
  agentic_v2?: boolean;
  tool_step?: Partial<ToolStep> | null;
  timeline?: ChatTimelineSegment[];
  /** Live in-app turn only — never persisted. */
  thinking?: boolean;
  /** Live in-app turn only — what the agent is doing while {@link thinking}. */
  thinking_status?: ThinkingStatus;
};

export type ChatTimelineSegment =
  | { type: "text"; content: string }
  | {
      type: "tool_step";
      tool_name: string;
      label?: string | null;
      tool_label?: string | null;
      status?: string | null;
      summary?: string | null;
      mcp_connector_id?: string | null;
      step_id?: string | null;
      kind?: "backend" | "fe" | "mcp" | null;
      fe_tool_id?: string | null;
      fe_tool_params?: Record<string, unknown> | null;
    }
  | { type: "embeds"; embeds: ChatEmbed[] };

export interface ChatMessage {
  id: string;
  content: string;
  sender: {
    id: string;
    name: string;
    fullname?: string;
    image?: string;
    type: string;
  };
  timestamp: number;
  miniUpload?: boolean;
  // UI-only fields (not persisted):
  uiKey?: string;
  isPlaceholder?: boolean;
  actions?: ChatMessageAction[];
  attachments?: ChatMessageAttachment[];
  sources?: Array<{
    document_name: string;
    document_id: string;
    document_date?: string;
    justification: string;
  }>;
  metadata?: ChatMessageMetadata;
}

interface LineRevealContentProps {
  html: string;
  className: string;
  enabled: boolean;
  onRevealStep?: () => void;
}

const LINE_REVEAL_MIN_DURATION_MS = 1100;
const LINE_REVEAL_MAX_DURATION_MS = 5200;
const LINE_REVEAL_MS_PER_LINE = 260;
const LINE_REVEAL_STEP_TRANSITION_MS = 90;
const LINE_REVEAL_BOTTOM_FADE_PX = 18;
const LINE_REVEAL_BOTTOM_PADDING_PX = 12;
const LINE_REVEAL_SHORT_MAX_LINES = 3;
const LINE_REVEAL_SHORT_MIN_DURATION_MS = 280;
const LINE_REVEAL_SHORT_MAX_DURATION_MS = 1100;
const LINE_REVEAL_SHORT_MS_PER_LINE = 140;
const CHAT_BOTTOM_THRESHOLD_PX = 24;

/** Mongo extended JSON id or primitive ids from REST/WS. */
export interface MongoOidId {
  $oid: string;
}

export type RawMessageId =
  | string
  | number
  | MongoOidId
  | null
  | undefined;

/** Canonical id string so REST history, WS payloads, and $oid shapes compare equal. */
export function normalizeMessageId(raw: RawMessageId): string {
  if (raw == null || raw === "") return "";
  if (typeof raw === "string") return raw;
  if (typeof raw === "number") return String(raw);
  if (typeof raw === "object" && "$oid" in raw && typeof raw.$oid === "string") {
    return raw.$oid;
  }
  return String(raw);
}

/** React list key / `data-message-id` when `id` is missing (avoids duplicate `key=""`). */
export function stableMessageRowKey(
  message: ChatMessage,
  indexInFullList: number,
): string {
  if (message.uiKey) return message.uiKey;
  const id = normalizeMessageId(message.id);
  if (id) return id;
  const idx = indexInFullList >= 0 ? indexInFullList : 0;
  return `row-${message.timestamp}-${idx}`;
}

interface ChatWsSender {
  id?: string;
  name?: string;
  type?: string;
}

/** Subset of interaction `full_document` from Mongo change streams. */
interface ChatInteractionFullDocument {
  _id?: RawMessageId;
  id?: RawMessageId;
  content?: string;
  sender?: ChatWsSender;
  role?: string;
  conversation_id?: string;
  pod_instance_id?: string;
  created_at?: string | number;
  timestamp?: string | number;
  updated_at?: string | number;
  actions?: ChatMessageAction[];
  attachments?: ChatMessageAttachment[];
  metadata?: ChatMessageMetadata;
}

interface ChatInteractionChangeEnvelope {
  full_document?: ChatInteractionFullDocument | null;
  operation?: string | number;
  document_id?: RawMessageId;
  timestamp?: string | number;
}

function parseChatInteractionChange(message: WebSocketMessage): {
  change: ChatInteractionChangeEnvelope;
  fullDoc: ChatInteractionFullDocument;
} | null {
  const envelope = wsMessageToPayloadRecord(message);
  if (!envelope) return null;
  const change = envelope as ChatInteractionChangeEnvelope;
  const fd = change.full_document;
  if (!fd || typeof fd !== "object") return null;
  return { change, fullDoc: fd };
}

const isChatScrolledToVisualBottom = (container: HTMLDivElement) =>
  Math.abs(container.scrollTop) <= CHAT_BOTTOM_THRESHOLD_PX;

const scrollChatToVisualBottom = (
  container: HTMLDivElement,
  behavior: ScrollBehavior = "auto",
) => {
  container.scrollTo({ top: 0, behavior });
};

/**
 * Reveals pre-rendered HTML by line-height increments without splitting content.
 */
const LineRevealContent: React.FC<LineRevealContentProps> = ({
  html,
  className,
  enabled,
  onRevealStep,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<number | null>(null);
  const lastHeightRef = useRef<number>(0);
  const lastVisibleLineRef = useRef<number>(0);
  const onRevealStepRef = useRef<typeof onRevealStep>(onRevealStep);
  const [maxHeightPx, setMaxHeightPx] = useState<number | null>(
    enabled ? 0 : null,
  );
  const [isRevealing, setIsRevealing] = useState<boolean>(enabled);

  useEffect(() => {
    onRevealStepRef.current = onRevealStep;
  }, [onRevealStep]);

  useLayoutEffect(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!enabled) {
      setMaxHeightPx(null);
      setIsRevealing(false);
      return;
    }

    const contentEl = contentRef.current;
    if (!contentEl) return;

    const computed = window.getComputedStyle(contentEl);
    const parsedLineHeight = Number.parseFloat(computed.lineHeight);
    const parsedFontSize = Number.parseFloat(computed.fontSize);
    const lineHeight = Number.isFinite(parsedLineHeight)
      ? parsedLineHeight
      : (Number.isFinite(parsedFontSize) ? parsedFontSize : 16) * 1.4;

    const totalHeight = contentEl.scrollHeight;
    const totalLines = Math.max(1, Math.ceil(totalHeight / lineHeight));
    const isShortMessage = totalLines <= LINE_REVEAL_SHORT_MAX_LINES;
    const durationMs = isShortMessage
      ? Math.min(
        LINE_REVEAL_SHORT_MAX_DURATION_MS,
        Math.max(
          LINE_REVEAL_SHORT_MIN_DURATION_MS,
          totalLines * LINE_REVEAL_SHORT_MS_PER_LINE,
        ),
      )
      : Math.min(
        LINE_REVEAL_MAX_DURATION_MS,
        Math.max(LINE_REVEAL_MIN_DURATION_MS, totalLines * LINE_REVEAL_MS_PER_LINE),
      );
    const stepIntervalMs = Math.max(
      80,
      Math.round(durationMs / Math.max(1, totalLines)),
    );
    const firstLineHeight = Math.ceil(lineHeight) + 2;
    const startHeight = Math.min(totalHeight, Math.max(8, firstLineHeight));

    // Single-line replies feel better with row fade only.
    if (totalLines <= 1) {
      lastHeightRef.current = totalHeight;
      lastVisibleLineRef.current = totalLines;
      setMaxHeightPx(null);
      setIsRevealing(false);
      return;
    }

    // Start with a small visible area to avoid blank flash.
    setIsRevealing(true);
    setMaxHeightPx(startHeight);
    lastHeightRef.current = startHeight;
    lastVisibleLineRef.current = 1;
    onRevealStepRef.current?.();

    let visibleLines = 1;
    intervalRef.current = window.setInterval(() => {
      visibleLines += 1;
      const clampedLines = Math.min(totalLines, visibleLines);
      const nextHeight = Math.min(
        totalHeight,
        Math.ceil(clampedLines * lineHeight) + 2,
      );

      if (nextHeight !== lastHeightRef.current) {
        lastHeightRef.current = nextHeight;
        setMaxHeightPx(nextHeight);
      }

      if (clampedLines !== lastVisibleLineRef.current) {
        lastVisibleLineRef.current = clampedLines;
        onRevealStepRef.current?.();
      }

      if (clampedLines >= totalLines || nextHeight >= totalHeight) {
        if (intervalRef.current !== null) {
          window.clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        lastHeightRef.current = totalHeight;
        lastVisibleLineRef.current = totalLines;
        // Drop max-height after reveal: a fixed pixel cap + overflow:hidden will clip
        // if scrollHeight grows later (scrollbar gutter, reflow, fonts, table layout).
        setMaxHeightPx(null);
        setIsRevealing(false);
      }
    }, stepIntervalMs);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsRevealing(false);
      setMaxHeightPx(null);
    };
  }, [enabled, html]);

  return (
    <div
      className="max-w-full min-w-0"
      style={
        enabled && maxHeightPx !== null
          ? {
            maxHeight: `${maxHeightPx}px`,
            overflow: "hidden",
            maxWidth: "100%",
            paddingBottom: `${LINE_REVEAL_BOTTOM_PADDING_PX}px`,
            willChange: "max-height",
            transition: `max-height ${LINE_REVEAL_STEP_TRANSITION_MS}ms linear`,
            ...(isRevealing
              ? {
                maskImage: `linear-gradient(to bottom, black 0%, black calc(100% - ${LINE_REVEAL_BOTTOM_FADE_PX}px), transparent 100%)`,
                WebkitMaskImage: `linear-gradient(to bottom, black 0%, black calc(100% - ${LINE_REVEAL_BOTTOM_FADE_PX}px), transparent 100%)`,
              }
              : {}),
          }
          : undefined
      }
    >
      <div ref={contentRef} className={className} dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};

// Define the state type
export type ChatState = {
  messages: ChatMessage[];
  isTyping: boolean;
  loading: boolean;
  error: string | null;
  refreshCounter: number;
};

/**
 * Parameters for determining whether the typing indicator should be shown.
 */
export interface TypingIndicatorParams {
  /** Map of active system jobs */
  activeSystemJobs: Map<string, ChatSystemJobRow>;
  /** Map of completed jobs to display */
  completedJobsToShow: Map<string, ChatSystemJobRow>;
  /** Flag indicating if waiting for a response after job completion */
  waitingForResponseAfterJob: boolean;
  /** Array of chat messages */
  messages: ChatMessage[];
  /** Current timestamp (allow injection for testing) */
  now?: number;
}

/**
 * Determines whether the typing indicator should be shown.
 * Extracted from useEffect for testability.
 *
 * Logic:
 * 1. If there are active (non-completed, non-failed) system jobs -> false
 * 2. If waiting for response after job OR recently completed jobs (< 2s) -> true
 * 3. If last user message is recent (< 30s) and no assistant response after it -> true
 * 4. Otherwise -> false
 *
 * @param params - The parameters for the typing indicator decision
 * @returns Whether the typing indicator should be shown
 */
export function shouldShowTypingIndicator(params: TypingIndicatorParams): boolean {
  const {
    activeSystemJobs,
    completedJobsToShow,
    waitingForResponseAfterJob,
    messages,
    now = Date.now(),
  } = params;

  // Check if there are active (non-completed, non-failed) system jobs
  const hasActiveJobs = Array.from(activeSystemJobs.values()).some(
    (job) => job.status !== "completed" && job.status !== "failed",
  );

  if (hasActiveJobs) {
    // Hide typing indicator if there are active system tasks
    return false;
  }

  // Check if there are recently completed jobs (within 2 seconds)
  const hasRecentlyCompletedJobs = Array.from(completedJobsToShow.values()).some(
    (job) =>
      typeof job.completedAt === "number" && now - job.completedAt < 2000,
  );

  if (waitingForResponseAfterJob || hasRecentlyCompletedJobs) {
    // Keep typing indicator if waiting for response after job completion
    return true;
  }

  // Check if we're waiting for a response from a user message
  let lastUserMessage: ChatMessage | null = null;
  let lastAssistantMessage: ChatMessage | null = null;

  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.isPlaceholder) {
      const placeholderHasReply =
        (typeof msg.content === "string" && msg.content.trim().length > 0) ||
        parseChatEmbeds(msg.metadata?.embeds).length > 0 ||
        parseToolStep(msg.metadata?.tool_step) !== null ||
        msg.metadata?.thinking !== undefined ||
        (Array.isArray(msg.metadata?.timeline) && msg.metadata.timeline.length > 0);
      if (
        placeholderHasReply &&
        (msg.sender.type === "assistant" || msg.sender.type === "worker") &&
        !lastAssistantMessage
      ) {
        lastAssistantMessage = msg;
      }
      continue;
    }
    if (msg.sender.type === "user" && !lastUserMessage) {
      lastUserMessage = msg;
    }
    if (
      (msg.sender.type === "assistant" || msg.sender.type === "worker") &&
      !lastAssistantMessage
    ) {
      lastAssistantMessage = msg;
    }
    if (lastUserMessage && lastAssistantMessage) break;
  }

  // Show typing indicator if:
  // 1. There's a recent user message (within 30 seconds)
  // 2. AND there's no assistant response after it
  if (lastUserMessage && lastUserMessage.timestamp > now - 30000) {
    const hasResponseAfterUserMessage =
      lastAssistantMessage &&
      lastAssistantMessage.timestamp > lastUserMessage.timestamp;

    return !hasResponseAfterUserMessage;
  }

  return false;
}

/**
 * Whether the user should not send another message yet (agent still working).
 * Broader than {@link shouldShowTypingIndicator}: active system jobs block sends
 * even when the typing dots are hidden.
 */
export function isAwaitingAgentResponse(params: TypingIndicatorParams): boolean {
  const { activeSystemJobs, messages } = params;

  const hasActiveJobs = Array.from(activeSystemJobs.values()).some(
    (job) => job.status !== "completed" && job.status !== "failed",
  );
  if (hasActiveJobs) return true;

  if (messages.some((m) => m.isPlaceholder)) return true;

  return shouldShowTypingIndicator(params);
}

// Define the action types
export type ChatAction =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; payload: ChatMessage[] }
  | { type: "FETCH_ERROR"; payload: string }
  | { type: "ADD_MESSAGE"; payload: ChatMessage }
  | { type: "SET_TYPING"; payload: boolean }
  | { type: "REPLACE_PLACEHOLDER"; payload: ChatMessage }
  | { type: "REMOVE_PLACEHOLDER" }
  | { type: "UPDATE_MESSAGE"; payload: { index: number; message: ChatMessage } }
  | { type: "REFRESH" };

/**
 * Reducer function for chat state management.
 * Handles message fetching, adding, updating, and typing indicator state.
 *
 * @param state - Current chat state
 * @param action - Action to perform
 * @returns Updated chat state
 */
export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return { ...state, loading: false, messages: action.payload };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "ADD_MESSAGE": {
      // Check if message already exists to prevent duplicates
      const messageExists = state.messages.some(
        (msg) => msg.id === action.payload.id,
      );
      if (messageExists) {
        return state;
      }

      return {
        ...state,
        messages: [...state.messages, action.payload],
      };
    }
    case "REPLACE_PLACEHOLDER": {
      const { payload: newMessage } = action;
      const messageIndex = state.messages.findIndex(
        (m) => (m as ChatMessage & { isPlaceholder?: boolean }).isPlaceholder,
      );

      if (messageIndex !== -1) {
        const nextMessages = [...state.messages];
        nextMessages[messageIndex] = newMessage;
        return { ...state, messages: nextMessages };
      } else {
        // No placeholder found - check if message already exists (avoid duplicates)
        const existingMessage = state.messages.find(
          (m) => m.id === newMessage.id || m.content === newMessage.content,
        );
        if (existingMessage) {
          return state; // Already have this message, skip
        }
        return { ...state, messages: [...state.messages, newMessage] };
      }
    }
    case "REMOVE_PLACEHOLDER":
      return {
        ...state,
        messages: state.messages.filter(
          (m) => !(m as ChatMessage & { isPlaceholder?: boolean }).isPlaceholder,
        ),
      };
    case "UPDATE_MESSAGE": {
      const { index, message } = action.payload;
      const nextMessages = [...state.messages];
      nextMessages[index] = message;
      return { ...state, messages: nextMessages };
    }
    case "SET_TYPING":
      return { ...state, isTyping: action.payload };
    case "REFRESH":
      return {
        ...state,
        loading: true,
        refreshCounter: state.refreshCounter + 1,
      };
    default:
      return state;
  }
}

interface ChatProps {
  recipientId: string;
  recipientName: string;
  recipientAvatar: string;
  conversationId?: string;
  conversationTitle?: string;
  isAni?: boolean;
  useDirectResponse?: boolean;
  miniUpload?: boolean;
  isMinimal?: boolean;
  sendMessage: (
    recipientId: string,
    data: {
      content: string;
      role: string;
      attachments?: ChatMessageAttachment[];
    },
  ) => Promise<ChatMessage>;
  fetchHistory: (recipientId: string) => Promise<ChatMessage[]>;
  fetchSystemTasks?: (recipientId: string) => Promise<SystemTask[]>;
  chatChannel?: string;
  systemTaskChannel?: string;
  samplePrompts?: string[];
  initialAssistantMessage?: string;
  isContextActive?: boolean;
  customAvatar?: React.ReactNode;
  onTypingChange?: (isTyping: boolean) => void;
  onCreateConversation?: () => Promise<string>;
  /** Route file uploads to agentic v2 conversation_documents (not pods). */
  useAgenticV2Documents?: boolean;
  richContext?: RichContext;
  contextLabel?: string;
  displayMode?: "normal" | "developer";
  showMcpDebugPanel?: boolean;
  showFeToolDebugPanel?: boolean;
  feToolDebugRunner?: FeToolDebugRunner;
  feToolDebugOsContext?: OsAniContextSnapshot;
  feOpenAppAllowlist?: readonly string[];
  isActive?: boolean;
  inputContainerClassName?: string;
  /** Callback that receives a refresh function when Chat mounts */
  onRefreshReady?: (refreshFn: () => Promise<void>) => void;
  /** Callback that receives a setTyping function when Chat mounts */
  onSetTypingReady?: (setTypingFn: (isTyping: boolean) => void) => void;
  /** Callback that receives an addMessage function when Chat mounts */
  onAddMessageReady?: (addMessageFn: (message: ChatMessage) => void) => void;
  /** Patch the in-flight assistant placeholder (e.g. SSE token streaming). */
  onUpdatePlaceholderReady?: (
    updateFn: (updates: Pick<ChatMessage, "content" | "metadata">) => void,
  ) => void;
  /** Callback fired when Ani working state changes (based on system jobs) */
  onAniWorkingChange?: (isWorking: boolean) => void;
  /** When false, skips system-jobs WebSocket subscription and polling (e.g. worker chat). */
  enableSystemJobs?: boolean;
  /** Ani tone mode; use with {@link onAniPersonalityModeChange} for minimal Ani composer. */
  aniPersonalityMode?: AniPersonalityMode;
  /** Updates Ani tone when the user picks a mode in the composer. */
  onAniPersonalityModeChange?: (mode: AniPersonalityMode) => void;
  /**
   * When set, the chat sends this as a user message once (then caller should clear via
   * {@link onOutboundUserMessageConsumed}).
   */
  outboundUserMessage?: OutboundUserMessagePayload | null;
  /** Called immediately before sending {@link outboundUserMessage} so the queue can clear. */
  onOutboundUserMessageConsumed?: () => void;
  /** Replay a frontend tool from a tool-step badge (Floating Ani). */
  onReplayFeTool?: (toolId: string, params: Record<string, unknown>) => void;
  /** Render assistant prose as GFM markdown (Floating Ani sidebar). */
  enableAssistantMarkdown?: boolean;
  /** Floating Ani sidebar timeline layout: no avatar indent on tool rows, tight embed spacing. */
  compactTimeline?: boolean;
}

// Chat messages are sanitised with sanitizeRichHtml before injection.
// Helper function to get file icon and color based on MIME type or filename
const getFileIconAndColor = (mimeType?: string, filename?: string) => {
  // Check MIME type first, then fall back to filename extension
  if (mimeType?.includes("pdf") || filename?.endsWith(".pdf")) {
    return { icon: faFilePdf, color: "text-red-500" };
  }
  if (
    mimeType?.includes("wordprocessingml") ||
    mimeType?.includes("msword") ||
    filename?.endsWith(".docx") ||
    filename?.endsWith(".doc")
  ) {
    return { icon: faFileWord, color: "text-blue-500" };
  }
  if (
    mimeType?.includes("spreadsheetml") ||
    mimeType?.includes("excel") ||
    filename?.endsWith(".xlsx") ||
    filename?.endsWith(".xls")
  ) {
    return { icon: faFileExcel, color: "text-green-600" };
  }
  if (mimeType?.includes("csv") || filename?.endsWith(".csv")) {
    return { icon: faFileCsv, color: "text-green-500" };
  }
  if (
    mimeType?.includes("presentationml") ||
    mimeType?.includes("powerpoint") ||
    filename?.endsWith(".pptx") ||
    filename?.endsWith(".ppt")
  ) {
    return { icon: faFilePowerpoint, color: "text-orange-500" };
  }
  // Final fallback to PDF (most common document type)
  return { icon: faFilePdf, color: "text-red-500" };
};

// Map of known action labels to their file type info (used when metadata is missing)
const LABEL_FILE_TYPE_MAP: Record<
  string,
  { extension: string; mimeType: string }
> = {
  "Download PDF": { extension: ".pdf", mimeType: "application/pdf" },
  "Download Draft Narrative": {
    extension: ".docx",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  "Download Suitability Letter": {
    extension: ".docx",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  "Download Advisor Notes": {
    extension: ".docx",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  "Download Excel Report": {
    extension: ".xlsx",
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
  "Download CSV Report": {
    extension: ".csv",
    mimeType: "text/csv",
  },
};

import { WS_CHANNELS } from "../../utils/websocket";
import type {
  AniPersonalityMode,
  OutboundUserMessagePayload,
} from "@/context/AniContext";

const chatChannelUrl = WS_CHANNELS.CHAT;
const systemTaskChannelUrl = WS_CHANNELS.SYSTEM_TASKS;
const systemJobsChannelUrl = WS_CHANNELS.SYSTEM_TASKS;

export type RenderChatTimelineContentOptions = {
  isNewMessage?: boolean;
  onReplayFeTool?: (toolId: string, params: Record<string, unknown>) => void;
  useDirectResponse?: boolean;
  enableAssistantMarkdown?: boolean;
  compactTimeline?: boolean;
};

/** Live streamed text segment, revealed smoothly as deltas arrive. */
function StreamingTextSegment({ content }: { content: string }) {
  const smoothed = useSmoothText(content);
  return (
    <div
      className={CHAT_MESSAGE_CONTENT_CLASS}
      dangerouslySetInnerHTML={{ __html: sanitizeChatMessageHtml(trimPartialTag(smoothed)) }}
    />
  );
}

/**
 * Renders timeline segments and optional live thinking row for a chat message.
 */
export function renderChatTimelineMessageContent(
  message: ChatMessage,
  options: RenderChatTimelineContentOptions = {},
): React.ReactNode | null {
  const {
    isNewMessage = false,
    onReplayFeTool,
    useDirectResponse = false,
    enableAssistantMarkdown = false,
    compactTimeline = false,
  } = options;
  const timeline = message.metadata?.timeline;
  const showThinking = message.metadata?.thinking === true;
  if (!((timeline && timeline.length > 0) || showThinking)) {
    return null;
  }
  const isLivePlaceholder = Boolean(message.isPlaceholder);
  const skipLineReveal = useDirectResponse || enableAssistantMarkdown;
  const segments = timeline ?? [];
  const timelineNodes: React.ReactNode[] = [];
  const toolStepClass = compactTimeline ? "mb-1" : "pl-[37px] mb-1";

  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    if (segment.type === "text") {
      const hasContent = segment.content.trim().length > 0;
      if (hasContent) {
        if (isLivePlaceholder) {
          if (enableAssistantMarkdown) {
            timelineNodes.push(
              <ChatAssistantContent
                key={`tl-text-${index}`}
                content={segment.content}
                streaming={true}
                enableMarkdown={true}
              />,
            );
          } else {
            timelineNodes.push(
              <StreamingTextSegment key={`tl-text-${index}`} content={segment.content} />,
            );
          }
        } else if (skipLineReveal) {
          if (enableAssistantMarkdown) {
            timelineNodes.push(
              <ChatAssistantContent
                key={`tl-text-${index}`}
                content={segment.content}
                streaming={false}
                enableMarkdown={true}
              />,
            );
          } else {
            timelineNodes.push(
              <div
                key={`tl-text-${index}`}
                className={CHAT_MESSAGE_CONTENT_CLASS}
                dangerouslySetInnerHTML={{
                  __html: sanitizeChatMessageHtml(segment.content),
                }}
              />,
            );
          }
        } else {
          timelineNodes.push(
            <LineRevealContent
              key={`tl-text-${index}`}
              html={sanitizeChatMessageHtml(segment.content)}
              className={CHAT_MESSAGE_CONTENT_CLASS}
              enabled={isNewMessage && message.sender.type !== "user"}
            />,
          );
        }
      }
      continue;
    }
    if (segment.type === "tool_step") {
      const next = segments[index + 1];
      if (
        next?.type === "embeds" &&
        canMergeToolStepWithFollowingSearchEmbeds(segment, next.embeds)
      ) {
        const searchEmbeds = searchEmbedsFromSegment(next.embeds);
        timelineNodes.push(
          <div key={`tl-tool-search-${index}`} className={toolStepClass}>
            <ToolStepSearchEmbedRow
              toolSegment={segment as Record<string, unknown>}
              searchEmbeds={searchEmbeds}
              onReplayFeTool={onReplayFeTool}
            />
          </div>,
        );
        index += 1;
        continue;
      }
      if (
        next?.type === "embeds" &&
        canMergeToolStepWithFollowingDataTableEmbeds(segment, next.embeds)
      ) {
        const dataTableEmbeds = dataTableEmbedsFromSegment(next.embeds);
        timelineNodes.push(
          <div key={`tl-tool-datatable-${index}`} className={toolStepClass}>
            <ToolStepDataTableEmbedRow
              toolSegment={segment as Record<string, unknown>}
              dataTableEmbeds={dataTableEmbeds}
              messageId={`${message.id}-${index}`}
              onReplayFeTool={onReplayFeTool}
            />
          </div>,
        );
        index += 1;
        continue;
      }
      const step = parseToolStep(segment);
      if (step) {
        timelineNodes.push(
          <div key={`tl-tool-${index}`} className={toolStepClass}>
            <ToolStepRow step={step} onReplayFeTool={onReplayFeTool} />
          </div>,
        );
      }
      continue;
    }
    if (segment.type === "embeds") {
      const chatEmbeds = parseChatEmbeds(segment.embeds);
      if (chatEmbeds.length) {
        timelineNodes.push(
          compactTimeline ? (
            <div key={`tl-embeds-${index}`} className="mb-1">
              <ChatEmbeds
                variant="timeline"
                embeds={chatEmbeds}
                messageId={`${message.id}-${index}`}
              />
            </div>
          ) : (
            <ChatEmbeds key={`tl-embeds-${index}`} embeds={chatEmbeds} messageId={`${message.id}-${index}`} />
          ),
        );
      }
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {timelineNodes}
      {showThinking ? (
        <div className="mb-1">
          <ThinkingRow status={message.metadata?.thinking_status} />
        </div>
      ) : null}
    </div>
  );
}

export function Chat({
  recipientId,
  recipientName,
  recipientAvatar: _recipientAvatar,
  conversationId,
  conversationTitle,
  isAni,
  useDirectResponse = false,
  miniUpload = false,
  isMinimal = false,
  sendMessage,
  fetchHistory,
  fetchSystemTasks: _fetchSystemTasks,
  chatChannel = chatChannelUrl,
  systemTaskChannel: _systemTaskChannel = systemTaskChannelUrl,
  samplePrompts,
  initialAssistantMessage,
  isContextActive = true,
  customAvatar,
  onTypingChange,
  onCreateConversation,
  useAgenticV2Documents = false,
  richContext,
  contextLabel,
  displayMode = "normal",
  showMcpDebugPanel = false,
  showFeToolDebugPanel = false,
  feToolDebugRunner,
  feToolDebugOsContext,
  feOpenAppAllowlist,
  isActive = true,
  inputContainerClassName,
  onRefreshReady,
  onSetTypingReady,
  onAddMessageReady,
  onUpdatePlaceholderReady,
  onAniWorkingChange,
  aniPersonalityMode,
  onAniPersonalityModeChange,
  outboundUserMessage,
  onOutboundUserMessageConsumed,
  enableSystemJobs = true,
  onReplayFeTool,
  enableAssistantMarkdown = false,
  compactTimeline = false,
}: ChatProps) {
  const { user, isAuthenticated, webSocketAuthReady } = useAuth();
  const isAdmin = useIsAdmin();
  const { profileCompanyId: staffProfileCompanyId } = useFloatingAniProfileCompanyId(
    user?.company_id === "ani" && isAdmin,
  );
  const effectiveAgentPayloadCompanyId = staffProfileCompanyId || user?.company_id;
  const [downloadingActionId, setDownloadingActionId] = useState<string | null>(
    null,
  );
  const [downloadingSourceId, setDownloadingSourceId] = useState<string | null>(
    null,
  );
  const [activeSystemJobs, setActiveSystemJobs] = useState<
    Map<string, ChatSystemJobRow>
  >(new Map());
  const [completedJobsToShow, setCompletedJobsToShow] = useState<
    Map<string, ChatSystemJobRow>
  >(new Map());

  const [expandedSources, setExpandedSources] = useState<
    Record<string, boolean>
  >({});
  const [showAllSources, setShowAllSources] = useState<Record<string, boolean>>(
    {},
  );

  // Track if we're waiting for a response after job completion
  const [waitingForResponseAfterJob, setWaitingForResponseAfterJob] =
    useState(false);
  // Track timeouts for completed jobs
  const completedJobTimeoutsRef = useRef(new Map());
  // Track processed job completion events to prevent duplicates
  const processedCompletionEventsRef = useRef(new Set());
  // Track completed jobs that have been permanently cleared from UI
  const permanentlyClearedJobIdsRef = useRef(new Set());
  // Fallback timeout for clearing typing indicator when WebSocket doesn't deliver
  const typingFallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize state with reducer
  const initialState: ChatState = {
    messages: [],
    isTyping: false,
    loading: true,
    error: null,
    refreshCounter: 0,
  };

  const [state, dispatch] = useReducer(chatReducer, initialState);
  const { lastMessage, subscribe, unsubscribe, status } = useWebSocket();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const placeholderIdRef = useRef<string | null>(null);
  const isSendInFlightRef = useRef(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  /** Single assistant message id allowed to run line-reveal / scroll; cleared on history load. */
  const [liveLineRevealMessageId, setLiveLineRevealMessageId] = useState<
    string | null
  >(null);
  /** Bumped after each successful history load so the WS effect can re-run while history is ready. */
  const [historyGateVersion, setHistoryGateVersion] = useState(0);
  /** False from FETCH_START until FETCH_SUCCESS finishes for this thread (WS can arrive during fetch). */
  const historyReadyForThreadRef = useRef(false);
  const messagesRef = useRef(state.messages);
  messagesRef.current = state.messages;
  // Deduplication for WS messages processed in this component
  const processedWsMessageKeysRef = useRef<Set<string>>(new Set());
  const lastProcessedRawMessageRef = useRef<WebSocketMessage | null>(null);

  // Scroll tracking for gradient masks
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollPinnedRef = useRef(true);
  const previousMessagesMetaRef = useRef<{
    count: number;
    latestMessageId: string | null;
  }>({ count: 0, latestMessageId: null });
  /** After FETCH_SUCCESS replaces the list, skip one smooth-scroll (not a live agent reply). */
  const skipNextLiveScrollAfterHistoryRef = useRef(false);
  const [, setShowTopGradient] = useState(false);
  const [, setShowBottomGradient] = useState(false);

  // Handle scroll to update gradient visibility
  const handleMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isScrollable = scrollHeight > clientHeight;

    autoScrollPinnedRef.current = isChatScrolledToVisualBottom(container);

    if (!isScrollable) {
      setShowTopGradient(false);
      setShowBottomGradient(false);
      return;
    }

    // Calculate distances from edges
    const distanceFromTop = scrollTop;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    // Show gradients where content is cut off
    // Top gradient: show when not at the very top (content above)
    setShowTopGradient(distanceFromTop > 5);
    // Bottom gradient: show when not at the very bottom (content below)
    setShowBottomGradient(distanceFromBottom > 5);
  }, []);

  const initialMessageIds = useRef<Set<string>>(new Set());

  // Determine the last non-user assistant message for placeholder naming
  const lastNonUserAssistantMessage = useMemo(() => {
    for (let i = state.messages.length - 1; i >= 0; i--) {
      const message = state.messages[i];
      if (
        message.sender.type === "assistant" ||
        message.sender.type === "worker"
      ) {
        return message;
      }
    }
    return null;
  }, [state.messages]);

  // Refs to track component lifecycle and prevent duplicate requests
  const isMounted = useRef(true);
  const isLoadingRef = useRef(false);
  const previousRecipientId = useRef(recipientId);
  const previousConversationId = useRef(conversationId);
  const hasInitialLoad = useRef(false);

  // Function to load chat history
  const loadChatHistory = useCallback(async () => {
    if (isLoadingRef.current || !isMounted.current) return;

    isLoadingRef.current = true;
    historyReadyForThreadRef.current = false;
    processedWsMessageKeysRef.current.clear();
    lastProcessedRawMessageRef.current = null;
    dispatch({ type: "FETCH_START" });
    setLiveLineRevealMessageId(null);

    try {
      const history = await fetchHistory(recipientId);
      const finalHistory = [...history].map((msg) => ({
        ...msg,
        id: normalizeMessageId(
          msg.id ??
            (msg as ChatMessage & { _id?: RawMessageId })._id,
        ),
        timestamp: (() => {
          // Try to get timestamp from various possible fields
          const timestampSource =
            msg.timestamp ||
            (msg as ChatMessage & { created_at?: string | number }).created_at ||
            (msg as ChatMessage & { timestamp?: string | number }).timestamp;

          if (!timestampSource) {
            console.warn("[Chat] No timestamp found for message:", msg.id);
            return Date.now(); // Fallback to current time
          }

          // Handle different timestamp formats
          let timestamp: number;

          if (typeof timestampSource === "number") {
            // If it's already a number (milliseconds since epoch)
            timestamp = timestampSource;
          } else if (typeof timestampSource === "string") {
            // If it's a string, try to parse it
            const date = new Date(timestampSource);
            timestamp = date.getTime();
          } else {
            console.warn(
              "[Chat] Invalid timestamp format for message:",
              msg.id,
              timestampSource,
            );
            return Date.now();
          }

          // Validate the timestamp
          if (isNaN(timestamp) || timestamp <= 0) {
            console.warn(
              "[Chat] Invalid timestamp value for message:",
              msg.id,
              timestamp,
            );
            return Date.now();
          }

          return timestamp;
        })(),
      })); // Create a new array to avoid mutation issues and validate timestamps

      // Add initial assistant message if provided and no history exists
      if (initialAssistantMessage && history.length === 0) {
        const initialMessage: ChatMessage = {
          id: "initial-assistant-message",
          content: initialAssistantMessage,
          sender: {
            id: "ani",
            name: recipientName, // Use recipientName for Ani's name
            type: "assistant",
          },
          timestamp: Date.now(),
        };
        // Prepend to ensure it appears first
        finalHistory.unshift(initialMessage);
      }

      if (isMounted.current) {
        skipNextLiveScrollAfterHistoryRef.current = true;
        dispatch({ type: "FETCH_SUCCESS", payload: finalHistory });
        hasInitialLoad.current = true;

        // Store the IDs of messages loaded initially
        initialMessageIds.current = new Set(
          finalHistory.map((msg) => normalizeMessageId(msg.id)),
        );
        historyReadyForThreadRef.current = true;
        setHistoryGateVersion((v) => v + 1);

        // Mark initial load as complete
        setInitialLoadComplete(true);

        // Land on the latest messages after loading history without playing
        // a smooth-scroll animation when switching conversations.
        setTimeout(() => {
          if (messagesContainerRef.current) {
            scrollChatToVisualBottom(messagesContainerRef.current);
            autoScrollPinnedRef.current = true;
            handleMessagesScroll();
          }
        }, 100); // Small delay to ensure DOM is updated
      }
    } catch (err) {
      console.error("Error fetching chat history:", err);
      if (isMounted.current) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load chat history";
        dispatch({ type: "FETCH_ERROR", payload: errorMessage });
        historyReadyForThreadRef.current = true;
        setHistoryGateVersion((v) => v + 1);
      }
    } finally {
      isLoadingRef.current = false;
    }
  }, [
    recipientId,
    fetchHistory,
    initialAssistantMessage,
    recipientName,
    handleMessagesScroll,
  ]);

  // Expose refresh function to parent via callback
  useEffect(() => {
    if (onRefreshReady) {
      onRefreshReady(loadChatHistory);
    }
  }, [onRefreshReady, loadChatHistory]);

  // Expose setTyping function to parent via callback
  useEffect(() => {
    if (onSetTypingReady) {
      onSetTypingReady((isTyping: boolean) => {
        dispatch({ type: "SET_TYPING", payload: isTyping });
      });
    }
  }, [onSetTypingReady]);

  // Expose addMessage function to parent via callback
  useEffect(() => {
    if (onAddMessageReady) {
      onAddMessageReady((message: ChatMessage) => {
        dispatch({ type: "ADD_MESSAGE", payload: message });
      });
    }
  }, [onAddMessageReady]);

  useLayoutEffect(() => {
    if (!onUpdatePlaceholderReady) {
      return;
    }
    onUpdatePlaceholderReady((updates) => {
      const messageIndex = messagesRef.current.findIndex(
        (m) => (m as ChatMessage & { isPlaceholder?: boolean }).isPlaceholder,
      );
      if (messageIndex === -1) {
        return;
      }
      const current = messagesRef.current[messageIndex];
      dispatch({
        type: "UPDATE_MESSAGE",
        payload: {
          index: messageIndex,
          message: {
            ...current,
            content: updates.content ?? current.content,
            metadata: updates.metadata ?? current.metadata,
          },
        },
      });
    });
  }, [onUpdatePlaceholderReady]);

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Subscribe to system jobs channel (pods only; workers use chat WS for responses)
  useEffect(() => {
    if (!isActive || !enableSystemJobs) return;
    if (!canAttemptWebSocketAuth(isAuthenticated, webSocketAuthReady)) return;

    subscribe(systemJobsChannelUrl);

    return () => {
      unsubscribe(systemJobsChannelUrl);
    };
  }, [subscribe, unsubscribe, isActive, enableSystemJobs, isAuthenticated, webSocketAuthReady]);

  // Ensure chat channel is subscribed when Chat component is mounted
  useEffect(() => {
    if (!chatChannel || !isActive) return;
    if (!canAttemptWebSocketAuth(isAuthenticated, webSocketAuthReady)) return;
    subscribe(chatChannel);
    return () => {
      unsubscribe(chatChannel);
    };
  }, [subscribe, unsubscribe, chatChannel, isActive, isAuthenticated, webSocketAuthReady]);

  // Effect to handle initial load, recipient changes, and conversation changes
  useEffect(() => {
    // Only load if:
    // 1. It's the initial load and we haven't loaded yet, OR
    // 2. The recipient has actually changed, OR
    // 3. The conversation has changed, OR
    // 4. There's a manual refresh
    const shouldLoad =
      !hasInitialLoad.current ||
      previousRecipientId.current !== recipientId ||
      previousConversationId.current !== conversationId ||
      state.refreshCounter > 0;

    if (shouldLoad) {
      previousRecipientId.current = recipientId;
      previousConversationId.current = conversationId;
      loadChatHistory();
    }
  }, [recipientId, conversationId, state.refreshCounter, loadChatHistory]);

  // Load active system jobs on mount and when conversation changes
  // Skip for frontend sidebar sessions (fe_*) - they don't have system jobs
  useEffect(() => {
    if (!enableSystemJobs) return;

    const targetId = conversationId || recipientId;

    // Skip if no target, general, or frontend sidebar session
    if (!targetId || targetId === "general" || targetId.startsWith("fe_")) return;

    const loadSystemJobs = async () => {
      try {
        const isConversation = !!conversationId;
        const systemJobs = await getAgenticSystemJobs(targetId, isConversation);

        const activeJobs = systemJobs.filter(
          (job) =>
            job.status !== "completed" &&
            job.status !== "failed" &&
            job.description,
        );

        setActiveSystemJobs(
          new Map(
            activeJobs.map((job) => [job._id, systemJobToChatRow(job)]),
          ),
        );
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return;
        }
        console.error("Error loading system jobs:", error);
      }
    };

    loadSystemJobs();
  }, [conversationId, recipientId, enableSystemJobs]);

  // Notify parent of typing state changes
  useEffect(() => {
    if (typeof onTypingChange === "function") {
      onTypingChange(state.isTyping);
    }
  }, [state.isTyping, onTypingChange]);

  // Notify parent of aniWorking state changes
  useEffect(() => {
    if (typeof onAniWorkingChange === "function") {
      // Ani is working when there are active system jobs (not completed/failed)
      const hasActiveJobs = Array.from(activeSystemJobs.values()).some(
        (job) => job.status !== "completed" && job.status !== "failed",
      );
      onAniWorkingChange(hasActiveJobs);
    }
  }, [activeSystemJobs, onAniWorkingChange]);

  // Update the useEffect to manage typing indicator based on system tasks
  useEffect(() => {
    const shouldShow = shouldShowTypingIndicator({
      activeSystemJobs,
      completedJobsToShow,
      waitingForResponseAfterJob,
      messages: state.messages,
    });
    dispatch({ type: "SET_TYPING", payload: shouldShow });
  }, [activeSystemJobs, state.messages, waitingForResponseAfterJob, completedJobsToShow]);

  const awaitingAgentResponse = useMemo(
    () =>
      isAwaitingAgentResponse({
        activeSystemJobs,
        completedJobsToShow,
        waitingForResponseAfterJob,
        messages: state.messages,
      }),
    [
      activeSystemJobs,
      completedJobsToShow,
      waitingForResponseAfterJob,
      state.messages,
    ],
  );

  const awaitingAgentResponseRef = useRef(false);
  awaitingAgentResponseRef.current = awaitingAgentResponse;

  const liveTurnPlaceholderActive = useMemo(
    () =>
      state.messages.some(
        (message) =>
          message.isPlaceholder && message.metadata?.thinking !== undefined,
      ),
    [state.messages],
  );

  const isComposerDisabled = state.loading || !isContextActive;

  const followUpPlaceholder = awaitingAgentResponse
    ? `Queue a follow-up — it will send after ${isAni ? "Ani" : recipientName} finishes with the current task`
    : undefined;

  const sendMessageNowRef = useRef<
    (content: string, attachments: ChatMessageAttachment[]) => Promise<void>
  >(async () => {});

  const { queue: outboundQueue, enqueue: enqueueOutbound, removeFromQueue } =
    useOutboundMessageQueue<ChatMessageAttachment>({
      isAgentBusy: awaitingAgentResponse,
      onSend: (content, attachments) =>
        sendMessageNowRef.current(content, attachments),
    });

  // Check scroll state when messages change
  useEffect(() => {
    // Use setTimeout to ensure DOM has updated
    const timer = setTimeout(() => {
      handleMessagesScroll();
    }, 100);
    return () => clearTimeout(timer);
  }, [state.messages, handleMessagesScroll]);

  // Initial check on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      handleMessagesScroll();
    }, 300);
    return () => clearTimeout(timer);
  }, [handleMessagesScroll]);

  // Clear line-reveal target after animation window so html updates do not re-trigger reveal.
  useEffect(() => {
    if (!liveLineRevealMessageId) return;
    const ms = LINE_REVEAL_MAX_DURATION_MS + 500;
    const t = window.setTimeout(() => setLiveLineRevealMessageId(null), ms);
    return () => window.clearTimeout(t);
  }, [liveLineRevealMessageId]);

  // Handle incoming messages from WebSocket only if not using direct response
  useEffect(() => {
    if (!isMounted.current || useDirectResponse) return;
    if (!historyReadyForThreadRef.current) return;

    const message = lastMessage?.get(chatChannel);
    if (!message) return;

    // Prevent processing the exact same message object multiple times due to re-renders
    if (lastProcessedRawMessageRef.current === message) {
      return;
    }
    lastProcessedRawMessageRef.current = message;

    try {
      const parsed = parseChatInteractionChange(message);
      if (!parsed) return;

      const { change: changeData, fullDoc } = parsed;

      const opRaw = changeData.operation;
      const opForDedupe =
        typeof opRaw === "string" ? opRaw : String(opRaw ?? "");
      const opLower = opForDedupe.toLowerCase();
      const isInsertLike = opLower === "" || opLower === "insert";

      // Build a stable key for deduplication using id + op + timestamp
      const stableId =
        normalizeMessageId(
          fullDoc._id ?? fullDoc.id ?? changeData.document_id ?? "",
        ) || "noid";
      const ts =
        fullDoc.updated_at || fullDoc.timestamp || changeData.timestamp || "";
      const dedupeKey = `${chatChannel}-${stableId}-${opForDedupe}-${ts}`;
      if (processedWsMessageKeysRef.current.has(dedupeKey)) {
        return;
      }
      processedWsMessageKeysRef.current.add(dedupeKey);

      // Verify this message is for the current conversation.
      // Prioritize conversationId if it exists, otherwise fallback to recipientId.
      if (conversationId) {
        if (fullDoc.conversation_id !== conversationId) {
          return;
        }
      } else if (fullDoc.pod_instance_id !== recipientId) {
        return;
      }

      // Handle both sender and role field formats
      let senderType: string | undefined =
        fullDoc.sender?.type ?? fullDoc.role;

      // For pods chat, if senderType is not set, assume it's an assistant message
      // since user messages are handled optimistically
      if (!senderType && fullDoc.pod_instance_id) {
        senderType = "assistant";
      }

      const resolvedSenderType = senderType ?? "assistant";

      // If the message from WebSocket is from the current user, skip adding it,
      // as it was already added optimistically.
      if (resolvedSenderType === "user") {
        // The typing indicator should remain ON, awaiting a response from a non-user.
        return;
      }

      const normId = normalizeMessageId(fullDoc._id ?? fullDoc.id);

      // Create chat message from the full_document
      const chatMessage: ChatMessage = {
        id: normId,
        content: fullDoc.content ?? "",
        sender: {
          id: fullDoc.sender?.id || "ani",
          name:
            fullDoc.sender?.name ||
            (resolvedSenderType === "assistant" ? recipientName : "Unknown"),
          type: resolvedSenderType,
        },
        timestamp: (() => {
          // Try to get timestamp from various possible fields
          const timestampSource = fullDoc.created_at || fullDoc.timestamp;

          if (!timestampSource) {
            return Date.now(); // Fallback to current time
          }

          // Handle different timestamp formats
          let timestamp: number;

          if (typeof timestampSource === "number") {
            // If it's already a number (milliseconds since epoch)
            timestamp = timestampSource;
          } else if (typeof timestampSource === "string") {
            // WebSocket timestamps from MongoDB are UTC but may lack 'Z' suffix
            // Ensure proper UTC parsing by adding 'Z' if missing
            let utcTimestamp = timestampSource;
            if (
              !timestampSource.endsWith("Z") &&
              !timestampSource.includes("+") &&
              !timestampSource.includes("-", 10)
            ) {
              utcTimestamp = timestampSource + "Z";
            }
            const date = new Date(utcTimestamp);
            timestamp = date.getTime();
          } else {
            return Date.now();
          }

          // Validate the timestamp
          if (isNaN(timestamp) || timestamp <= 0) {
            return Date.now();
          }

          return timestamp;
        })(),
        actions: fullDoc.actions,
        attachments: fullDoc.attachments,
        metadata: fullDoc.metadata,
      };

      try {
        // Try to replace placeholder first
        const hasPlaceholder = messagesRef.current.some(
          (m) => (m as ChatMessage & { isPlaceholder?: boolean }).isPlaceholder,
        );

        // Line-reveal only for a fresh insert not present in the hydrated history snapshot
        if (
          isInsertLike &&
          !initialMessageIds.current.has(normId) &&
          (resolvedSenderType === "assistant" ||
            resolvedSenderType === "worker")
        ) {
          setLiveLineRevealMessageId(normId);
        }

        if (hasPlaceholder) {
          dispatch({ type: "REPLACE_PLACEHOLDER", payload: chatMessage });
        } else {
          dispatch({ type: "ADD_MESSAGE", payload: chatMessage });
        }

        // Always turn off typing indicator for assistant/worker messages
        if (
          resolvedSenderType === "assistant" ||
          resolvedSenderType === "worker"
        ) {
          // Clear fallback timeout if it exists (WS message arrived in time)
          if (typingFallbackTimeoutRef.current) {
            clearTimeout(typingFallbackTimeoutRef.current);
            typingFallbackTimeoutRef.current = null;
          }
          dispatch({ type: "SET_TYPING", payload: false });
          // Clear waiting for response flag when we receive a response
          setWaitingForResponseAfterJob(false);
          // Clear all completed jobs when response is received
          setCompletedJobsToShow(new Map());
          // Clear all pending timeouts
          completedJobTimeoutsRef.current.forEach((timeoutId) => {
            clearTimeout(timeoutId);
          });
          completedJobTimeoutsRef.current.clear();
          // Clear processed completion events
          processedCompletionEventsRef.current.clear();
        }
      } catch (error) {
        console.error("[Chat] WebSocket: Error processing message:", error);
      }
    } catch (error) {
      console.error(
        "[Chat] WebSocket: Error in placeholder detection logic:",
        error,
      );
    }
  }, [
    lastMessage,
    recipientId,
    conversationId,
    chatChannel,
    useDirectResponse,
    dispatch,
    historyGateVersion,
    recipientName,
  ]);

  // Debug WebSocket status for system jobs
  useEffect(() => {
    const systemJobsStatus = status?.get(systemJobsChannelUrl);
    if (systemJobsStatus?.state === "CONNECTED") {
      // Connection established - ready to receive system job updates
    }
  }, [status]);

  // Handle incoming system jobs from WebSocket
  useEffect(() => {
    if (!isMounted.current || !enableSystemJobs) return;

    const message = lastMessage?.get(systemJobsChannelUrl);
    if (!message) {
      return;
    }

    try {
      const envelope = wsMessageToPayloadRecord(message);
      if (!envelope) return;

      const fullDocRaw = readFullDocumentFromPayload(envelope);
      if (!fullDocRaw) return;

      const jobRow = mergeWsSystemJobFullDocument(fullDocRaw);
      const jobId = jobRow._id ?? jobRow.id;
      if (!jobId) return;

      // Check if this job is for the current conversation/pod
      // Prioritize conversationId if it exists, otherwise fallback to recipientId
      if (conversationId) {
        if (jobRow.conversation_id !== conversationId) {
          return;
        }
      } else if (recipientId && jobRow.pod_instance_id !== recipientId) {
        return;
      }

      // Handle both "insert" and "update" operations
      if (jobRow.status === "completed" || jobRow.status === "failed") {
        // If this job was permanently cleared, skip entirely
        if (permanentlyClearedJobIdsRef.current.has(jobId)) {
          return;
        }

        // Create a unique key for this completion event
        const completionEventKey = `${jobId}-${jobRow.status}-${jobRow.updated_at ||
          jobRow.completed_at ||
          Date.now()
          }`;

        // Check if this completion event was already processed
        if (processedCompletionEventsRef.current.has(completionEventKey)) {
          return;
        }
        processedCompletionEventsRef.current.add(completionEventKey);

        if (jobRow.status === "completed") {
          dispatch({ type: "SET_TYPING", payload: true });
          setWaitingForResponseAfterJob(true);
        }

        // Remove from active jobs immediately
        setActiveSystemJobs((prev) => {
          const next = new Map(prev);
          next.delete(jobId);
          return next;
        });

        // Add to completed jobs for brief display only if not permanently cleared
        if (!permanentlyClearedJobIdsRef.current.has(jobId)) {
          setCompletedJobsToShow((prev) => {
            const next = new Map(prev);
            next.set(jobId, {
              ...jobRow,
              completedAt: Date.now(),
            });
            return next;
          });

          const timeoutId = setTimeout(() => {
            setCompletedJobsToShow((prev) => {
              const next = new Map(prev);
              next.delete(jobId);
              return next;
            });
            // Also remove from active jobs if it's still there somehow
            setActiveSystemJobs((prev) => {
              const next = new Map(prev);
              next.delete(jobId);
              return next;
            });
            completedJobTimeoutsRef.current.delete(jobId);
            // Mark as permanently cleared so it can never reappear later
            permanentlyClearedJobIdsRef.current.add(jobId);
          }, 1500);

          // Store timeout for later cleanup (clear any existing timeout first)
          if (completedJobTimeoutsRef.current.has(jobId)) {
            clearTimeout(completedJobTimeoutsRef.current.get(jobId));
          }
          completedJobTimeoutsRef.current.set(jobId, timeoutId);
        }
      } else if (jobRow.description && jobRow.status !== "pending") {
        // Add/update active jobs that have descriptions (handles both insert and update)
        // Store full document to have consistent data structure with initial load
        setActiveSystemJobs((prev) =>
          new Map(prev).set(jobId, jobRow),
        );
      }
      // No action needed for pending jobs or jobs without descriptions
    } catch (error) {
      console.error(
        "Error processing system job WebSocket message:",
        error,
        message,
      );
    }
  }, [lastMessage, conversationId, recipientId, enableSystemJobs]);

  // Scroll on live messages only. History loads set state.messages too, but
  // should not play the smooth scroll effect when browsing older conversations.
  useEffect(() => {
    const latestMessage = state.messages[state.messages.length - 1];
    const previousMeta = previousMessagesMetaRef.current;

    previousMessagesMetaRef.current = {
      count: state.messages.length,
      latestMessageId: latestMessage?.id ?? null,
    };

    if (state.loading) return;

    if (!initialLoadComplete || !latestMessage) return;

    const hasNewMessage =
      state.messages.length > previousMeta.count ||
      latestMessage.id !== previousMeta.latestMessageId;

    if (!hasNewMessage) return;

    if (skipNextLiveScrollAfterHistoryRef.current) {
      skipNextLiveScrollAfterHistoryRef.current = false;
      return;
    }

    // Check if user has active text selection
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }

    const container = messagesContainerRef.current;
    if (!container) return;

    const userSentMessage = latestMessage.sender.type === "user";
    if (!userSentMessage && !autoScrollPinnedRef.current) return;

    scrollChatToVisualBottom(container, "smooth");
    autoScrollPinnedRef.current = true;
  }, [state.messages, state.loading, initialLoadComplete]);

  // Format timestamp
  const formatTime = (timestamp: number) => {
    // Validate timestamp
    if (!timestamp || isNaN(timestamp) || timestamp <= 0) {
      return "Invalid Date";
    }

    const date = new Date(timestamp);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Parse sources from HTML content
  const parseSourcesFromContent = (
    content: string,
  ): Array<{
    document_name: string;
    document_id: string;
    document_date?: string;
    justification: string;
  }> => {
    let sourcesData = "";
    try {
      // Use a temporary DOM element to parse the HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = sanitizeRichHtml(content);

      // Find the sources section
      const sourcesSection = tempDiv.querySelector(".sources-section");
      if (!sourcesSection) return [];

      // Get the data-sources attribute
      sourcesData = sourcesSection.getAttribute("data-sources") || "";
      if (!sourcesData) return [];

      // Check for obviously incomplete JSON first
      if (
        !sourcesData.trim() ||
        (sourcesData.includes("[{") && !sourcesData.includes("}]"))
      ) {
        console.warn("Incomplete sources data detected:", sourcesData);
        return [];
      }

      // Try multiple parsing strategies
      const parseStrategies = [
        // Strategy 1: Parse as-is
        () => JSON.parse(sourcesData),

        // Strategy 2: Handle HTML entities
        () => {
          const decoded = sourcesData
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, "&");
          return JSON.parse(decoded);
        },

        // Strategy 3: Fix common apostrophe issues in single-quoted attributes
        () => {
          // Only apply this fix if we detect it's likely needed
          if (sourcesData.includes("'s ") || sourcesData.includes("'t ")) {
            const fixed = sourcesData.replace(
              /([a-zA-Z])'([st])\s/g,
              "$1\\'$2 ",
            );
            return JSON.parse(fixed);
          }
          throw new Error("Strategy 3 not applicable");
        },

        // Strategy 4: Try to fix truncated JSON arrays
        () => {
          if (sourcesData.startsWith("[") && !sourcesData.endsWith("]")) {
            // Attempt to close incomplete array
            const fixed = sourcesData + "]";
            return JSON.parse(fixed);
          }
          throw new Error("Strategy 4 not applicable");
        },
      ];

      // Try each strategy in order
      for (const strategy of parseStrategies) {
        try {
          return strategy();
        } catch {
          // Continue to next strategy
        }
      }

      console.warn("All parsing strategies failed for sources data:", {
        originalData: sourcesData,
        dataLength: sourcesData.length,
        startsWithArray: sourcesData.startsWith("["),
        endsWithArray: sourcesData.endsWith("]"),
        hasOpenBrace: sourcesData.includes("{"),
        hasCloseBrace: sourcesData.includes("}"),
      });
      return [];
    } catch (error) {
      console.error("Error parsing sources from content:", error);
      console.error("Raw sources data from attribute:", sourcesData);
      console.error(
        "Full data-sources match:",
        content.match(/data-sources=['"][^'"]*['"]/)?.[0],
      );
      return [];
    }
  };

  // Toggle sources accordion
  const toggleSourcesAccordion = (messageId: string) => {
    setExpandedSources((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  // Toggle showing all sources
  const toggleShowAllSources = (messageId: string) => {
    setShowAllSources((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  // Handle document download
  const handleDocumentDownload = async (
    documentId: string,
    documentName: string,
  ) => {
    const sourceId = `${documentId}-${documentName}`;
    setDownloadingSourceId(sourceId);

    try {
      // Use the standard documents download endpoint, let the backend handle the lookup logic
      const response = await apiRequestWithHeaders<Blob>({
        method: "GET",
        url: `/documents/download/${documentId}`,
        responseType: "blob",
        headers: {
          Accept: "application/octet-stream",
        },
      });

      // Use provided filename or extract from Content-Disposition header
      let finalFilename = documentName || documentId;

      // Try to extract filename from Content-Disposition header if not provided
      if (!documentName) {
        const contentDisposition =
          response.headers["content-disposition"] ||
          response.headers["Content-Disposition"] ||
          response.headers["CONTENT-DISPOSITION"];

        if (contentDisposition) {
          let filenameMatch = contentDisposition.match(
            /filename\*?=['"]?([^'";]+)['"]?/i,
          );
          if (!filenameMatch) {
            filenameMatch = contentDisposition.match(
              /filename=['"]([^'"]+)['"]/i,
            );
          }
          if (!filenameMatch) {
            filenameMatch = contentDisposition.match(/filename=([^;,\s]+)/i);
          }

          if (filenameMatch && filenameMatch[1]) {
            finalFilename = filenameMatch[1].trim().replace(/^['"]|['"]$/g, "");
          }
        }
      }

      // Create blob URL and trigger download
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = finalFilename;
      window.document.body?.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body?.removeChild(a);
    } catch (error) {
      console.error("Error downloading document:", error);
    } finally {
      setDownloadingSourceId(null);
    }
  };

  const sendMessageNow = async (
    content: string,
    attachments: ChatMessageAttachment[] = [],
  ) => {
    if (isSendInFlightRef.current) return;

    isSendInFlightRef.current = true;
    try {
      // Show typing indicator immediately when sending
      dispatch({ type: "SET_TYPING", payload: true });

      // Create local message object for immediate display
      const newMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        content,
        sender: {
          id: user?.id || "current-user",
          name: userDisplayName(user),
          image: user?.image,
          type: "user",
        },
        timestamp: Date.now(),
        attachments: attachments, // Include attachments in the optimistic update
      };

      // Add message to local state immediately for responsive UI
      dispatch({ type: "ADD_MESSAGE", payload: newMessage });

      // Insert a stable placeholder assistant message right away
      const placeholderId = `placeholder-${Date.now()}`;
      const placeholder: ChatMessage = {
        id: placeholderId,
        isPlaceholder: true,
        content: "",
        sender: {
          id: "assistant",
          name:
            lastNonUserAssistantMessage?.sender.name ||
            (isAni ? "Ani" : recipientName),
          type: lastNonUserAssistantMessage?.sender.type || "assistant",
        },
        timestamp: Date.now(),
      };
      placeholderIdRef.current = placeholderId;
      dispatch({ type: "ADD_MESSAGE", payload: placeholder });

      // Send message through the API
      const response = await sendMessage(recipientId, {
        content,
        role: "user",
        attachments,
      });

      // If using direct response, add it immediately
      if (useDirectResponse) {
        // Skip if response is empty (messages already shown via refresh)
        const responseHasTimeline = Boolean(response.metadata?.timeline?.length);
        if ((response.content || responseHasTimeline) && response.id) {
          const placeholderIndex = messagesRef.current.findIndex(
            (m) => (m as ChatMessage & { isPlaceholder?: boolean }).isPlaceholder,
          );
          const streamedPlaceholder =
            placeholderIndex !== -1 ? messagesRef.current[placeholderIndex] : undefined;
          const streamedTimeline =
            streamedPlaceholder &&
            Array.isArray(streamedPlaceholder.metadata?.timeline) &&
            streamedPlaceholder.metadata.timeline.length > 0;
          const alreadyStreamed =
            streamedPlaceholder &&
            ((typeof streamedPlaceholder.content === "string" &&
              streamedPlaceholder.content.trim().length > 0) ||
              streamedTimeline);

          const finalizeMetadata = (
            metadata?: ChatMessageMetadata,
          ): ChatMessageMetadata | undefined => {
            if (!metadata) {
              return metadata;
            }
            if (metadata.thinking === undefined) {
              return metadata;
            }
            const { thinking: _thinking, thinking_status: _thinkingStatus, ...rest } = metadata;
            return rest;
          };

          if (alreadyStreamed && placeholderIndex !== -1) {
            dispatch({
              type: "UPDATE_MESSAGE",
              payload: {
                index: placeholderIndex,
                message: {
                  ...streamedPlaceholder,
                  ...response,
                  id: normalizeMessageId(response.id),
                  isPlaceholder: false,
                  content: response.content,
                  metadata: finalizeMetadata(
                    response.metadata ?? streamedPlaceholder.metadata,
                  ),
                },
              },
            });
          } else {
            setLiveLineRevealMessageId(normalizeMessageId(response.id));
            dispatch({
              type: "REPLACE_PLACEHOLDER",
              payload: {
                ...response,
                id: normalizeMessageId(response.id),
              },
            });
          }
        }
        // Clear placeholder ref
        placeholderIdRef.current = null;
        // Always turn off typing indicator when response is received
        dispatch({ type: "SET_TYPING", payload: false });
        // Clear waiting for response flag when we receive a response
        setWaitingForResponseAfterJob(false);
        // Clear all completed jobs when response is received
        setCompletedJobsToShow(new Map());
        // Clear all pending timeouts
        completedJobTimeoutsRef.current.forEach((timeoutId) => {
          clearTimeout(timeoutId);
        });
        completedJobTimeoutsRef.current.clear();
        // Clear processed completion events
        processedCompletionEventsRef.current.clear();
      } else {
        // Fallback: If WebSocket message doesn't arrive within 5 seconds, clear typing indicator
        // This handles cases where backend hasn't implemented WebSocket yet or conversation_id mismatch
        if (typingFallbackTimeoutRef.current) {
          clearTimeout(typingFallbackTimeoutRef.current);
        }
        typingFallbackTimeoutRef.current = setTimeout(() => {
          dispatch({ type: "SET_TYPING", payload: false });
          placeholderIdRef.current = null;
          typingFallbackTimeoutRef.current = null;
        }, 5000);
      }
    } catch {
      if (isMounted.current) {
        dispatch({ type: "REMOVE_PLACEHOLDER" });
        placeholderIdRef.current = null;
        dispatch({
          type: "FETCH_ERROR",
          payload: "Failed to send message. Please try again.",
        });
        // Hide typing indicator if there's an error
        dispatch({ type: "SET_TYPING", payload: false });
      }
    } finally {
      isSendInFlightRef.current = false;
    }
  };

  sendMessageNowRef.current = sendMessageNow;

  const submitOutboundMessage = async (
    content: string,
    attachments: ChatMessageAttachment[] = [],
  ) => {
    if (awaitingAgentResponseRef.current || isSendInFlightRef.current) {
      enqueueOutbound(content, attachments);
      return;
    }
    await sendMessageNow(content, attachments);
  };

  const submitOutboundMessageRef = useRef(submitOutboundMessage);
  submitOutboundMessageRef.current = submitOutboundMessage;

  const showSamplePrompts =
    isAni &&
    Boolean(samplePrompts?.length) &&
    displayMode !== "developer" &&
    !state.messages.some((message) => message.id !== "initial-assistant-message");

  useEffect(() => {
    if (!outboundUserMessage || !isActive) return;
    const text = outboundUserMessage.content.trim();
    if (!text) return;
    onOutboundUserMessageConsumed?.();
    void submitOutboundMessageRef.current(text);
  }, [outboundUserMessage, isActive, onOutboundUserMessageConsumed]);

  // Render message content
  const renderMessageContent = (message: ChatMessage, isNewMessage = false) => {
    const timelineContent = renderChatTimelineMessageContent(message, {
      isNewMessage,
      onReplayFeTool,
      useDirectResponse,
      enableAssistantMarkdown,
      compactTimeline,
    });
    if (timelineContent) {
      return timelineContent;
    }

    const toolStep = parseToolStep(message.metadata?.tool_step);
    if (toolStep) {
      return <ToolStepRow step={toolStep} onReplayFeTool={onReplayFeTool} />;
    }
    const textToDisplay = message.content;
    const hasContent = typeof textToDisplay === "string" && textToDisplay.trim().length > 0;
    const chatEmbeds = parseChatEmbeds(message.metadata?.embeds);
    const embedsNode =
      chatEmbeds.length > 0 ? (
        <ChatEmbeds embeds={chatEmbeds} messageId={message.id} />
      ) : null;
    const shouldRevealByLine =
      isNewMessage &&
      message.sender.type !== "user" &&
      hasContent &&
      !useDirectResponse;

    const scrollDuringReveal = () => {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) return;

      const container = messagesContainerRef.current;
      if (container) {
        if (!autoScrollPinnedRef.current) return;

        // Chat list uses flex-col-reverse, so scrollTop=0 keeps the newest
        // content pinned to the visual bottom while still allowing manual scroll.
        scrollChatToVisualBottom(container);
        window.requestAnimationFrame(() => {
          if (messagesContainerRef.current && autoScrollPinnedRef.current) {
            scrollChatToVisualBottom(messagesContainerRef.current);
          }
        });
        return;
      }

      if (!messagesEndRef.current) return;
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
    };

    const textContentNode = hasContent
      ? useDirectResponse
        ? enableAssistantMarkdown
          ? (
              <ChatAssistantContent content={textToDisplay} enableMarkdown={true} />
            )
          : (
              <div
                className={CHAT_MESSAGE_CONTENT_CLASS}
                dangerouslySetInnerHTML={{
                  __html: sanitizeChatMessageHtml(textToDisplay),
                }}
              />
            )
        : (
            <LineRevealContent
              html={sanitizeChatMessageHtml(textToDisplay)}
              className={CHAT_MESSAGE_CONTENT_CLASS}
              enabled={shouldRevealByLine}
              onRevealStep={scrollDuringReveal}
            />
          )
      : null;

    // Render attachments if present
    const attachments = message.attachments &&
      message.attachments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {message.attachments.map((attachment, idx) => (
            <div
              key={`${attachment.document_id}-${idx}`}
              className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded px-3 py-1.5 text-sm"
            >
              <FontAwesomeIcon icon={faFile} className="text-blue-500" />
              <span className="max-w-[150px] truncate">
                {attachment.display_name || "Document"}
              </span>
              {attachment.size && (
                <span className="text-gray-500 text-xs">
                  ({Math.round(attachment.size / 1024)}KB)
                </span>
              )}
            </div>
          ))}
        </div>
      );

    // Parse and render sources if present in content
    const sources = parseSourcesFromContent(message.content);
    const sourcesDisplay =
      sources.length > 0 &&
      (() => {
        const INITIAL_SOURCES_LIMIT = 5;
        const shouldShowAll = showAllSources[message.id] || false;
        const displayedSources = shouldShowAll
          ? sources
          : sources.slice(0, INITIAL_SOURCES_LIMIT);
        const hasMoreSources = sources.length > INITIAL_SOURCES_LIMIT;

        return (
          <div className="mt-4 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <div
              className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors duration-200"
              role="button"
              tabIndex={0}
              onClick={() => toggleSourcesAccordion(message.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleSourcesAccordion(message.id);
                }
              }}
            >
              <div className="flex items-center gap-2">
                <span className="text-slate-600 dark:text-slate-400 text-sm font-medium uppercase tracking-wider">
                  Sources
                </span>
                <span className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-white text-xs px-2 py-0.5 rounded-full">
                  {sources.length}
                </span>
              </div>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`h-4 w-4 text-slate-500 transition-transform duration-200 ${expandedSources[message.id] ? "rotate-180" : ""
                  }`}
              />
            </div>
            <AnimatePresence initial={false}>
              {expandedSources[message.id] && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="divide-y divide-slate-200 dark:divide-slate-700">
                    {displayedSources.map((source, idx) => {
                      const sourceId = `${source.document_id}-${source.document_name}`;
                      const isDownloading = downloadingSourceId === sourceId;

                      return (
                        <div
                          key={`${source.document_id}-${idx}`}
                          className="p-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <FontAwesomeIcon
                                  icon={faFile}
                                  className="text-blue-500 text-sm flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <span className="font-medium text-slate-900 dark:text-slate-100 truncate block">
                                    {source.document_name}
                                  </span>
                                  {source.document_date && (
                                    <span className="text-xs text-slate-500 dark:text-slate-400">
                                      {new Date(
                                        source.document_date,
                                      ).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                      })}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                {source.justification}
                              </p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDocumentDownload(
                                  source.document_id,
                                  source.document_name,
                                );
                              }}
                              disabled={isDownloading}
                              className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-all duration-200 flex-shrink-0 ${isDownloading
                                ? "bg-blue-50 dark:bg-blue-900/20 text-blue-500 dark:text-blue-400 cursor-not-allowed"
                                : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 hover:scale-105 active:scale-95"
                                }`}
                            >
                              {isDownloading ? (
                                <Spinner size="sm" />
                              ) : (
                                <FontAwesomeIcon
                                  icon={faDownload}
                                  className="w-3 h-3"
                                />
                              )}
                              {isDownloading ? "Downloading..." : "Download"}
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {/* Show More/Less Button */}
                    {hasMoreSources && (
                      <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-slate-25 dark:bg-slate-800/25">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleShowAllSources(message.id);
                          }}
                          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors duration-200"
                        >
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`h-3 w-3 transition-transform duration-200 ${shouldShowAll ? "rotate-180" : ""
                              }`}
                          />
                          {shouldShowAll
                            ? `Show less (${sources.length - INITIAL_SOURCES_LIMIT
                            } hidden)`
                            : `Show ${sources.length - INITIAL_SOURCES_LIMIT
                            } more sources`}
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })();

    // Check if we have actions with view or download type
    if (message.actions && Array.isArray(message.actions)) {
      // Check if there are separate download actions (which handle downloads independently)
      const hasDownloadActions = message.actions.some(
        (action) =>
          action.type === "download" && isChatMessageActionWithUrl(action),
      );

      // Create action buttons for view actions (PDF with view + download)
      const viewActionButtons = message.actions
        .filter(
          (action): action is ChatMessageActionWithUrl =>
            action.type === "view" && isChatMessageActionWithUrl(action),
        )
        .map((action, index: number) => {
          const actionUrl = action.url;
          const actionId = `${message.id}-view-${index}`;

          // Check if this view action has the required params for PDF generation
          let canGeneratePdf = false;
          try {
            const urlObject = new URL(actionUrl, window.location.origin);
            const params = new URLSearchParams(urlObject.search);
            canGeneratePdf = !!(params.get("template") && params.get("config"));
          } catch {
            canGeneratePdf = false;
          }

          // Only show download button if: no separate download actions AND can generate PDF
          const showDownloadButton = !hasDownloadActions && canGeneratePdf;
          const showPdfIcon = shouldShowPdfIconOnViewAction(action.label);

          const handleDownloadClick = async (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setDownloadingActionId(actionId);

            try {
              const urlObject = new URL(actionUrl, window.location.origin);
              const params = new URLSearchParams(urlObject.search);
              const template = params.get("template");
              const config = params.get("config");
              const landscape = params.get("landscape") === "true";

              if (!template || !config) {
                console.error("Template or config not found in URL");
                setDownloadingActionId(null);
                return;
              }

              const blob = await PDFAPI.generatePDF({
                template,
                config,
                landscape,
              });

              const downloadUrl = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = downloadUrl;
              a.download = `${action.label || template || "document"}.pdf`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(downloadUrl);
              document.body.removeChild(a);
            } catch (error) {
              console.error("Error downloading PDF:", error);
              // You can add some user-facing error message here
            } finally {
              setDownloadingActionId(null);
            }
          };

          return (
            <div
              key={`view-${index}`}
              className="inline-block border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800 max-w-96"
            >
              <div
                className={`flex items-stretch ${showDownloadButton ? "divide-x divide-slate-200 dark:divide-slate-700" : ""}`}
              >
                <a
                  href={actionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-grow flex cursor-pointer items-center hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-200 min-w-0"
                >
                  {showPdfIcon && (
                    <div className="p-2 h-full flex items-center justify-center">
                      <FontAwesomeIcon
                        icon={faFilePdf}
                        className="text-lg text-slate-500"
                      />
                    </div>
                  )}
                  <div className="flex-grow truncate flex min-w-0 p-2 px-3">
                    <span className="font-medium text-sm dark:text-gray-200 truncate">
                      {action.label}
                    </span>
                  </div>
                  <div className="p-2 px-3">
                    <FontAwesomeIcon
                      icon={faExternalLinkAlt}
                      className="text-sm"
                    />
                  </div>
                </a>
                {showDownloadButton && (
                  <button
                    type="button"
                    onClick={handleDownloadClick}
                    className="flex cursor-pointer items-center justify-center p-2 px-3 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-200 disabled:cursor-not-allowed"
                    disabled={downloadingActionId === actionId}
                  >
                    {downloadingActionId === actionId ? (
                      <Spinner size="sm" />
                    ) : (
                      <FontAwesomeIcon icon={faDownload} className="text-sm" />
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        });

      // Create action buttons for download actions (direct file download, e.g., Word docs)
      const downloadActionButtons = message.actions
        .filter(
          (action): action is ChatMessageActionWithUrl =>
            action.type === "download" && isChatMessageActionWithUrl(action),
        )
        .map((action, index: number) => {
          const actionUrl = action.url;
          const actionId = `${message.id}-download-${index}`;
          const handleDirectDownload = async (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setDownloadingActionId(actionId);

            try {
              // Parse the URL to extract the path
              let downloadPath: string = actionUrl;

              try {
                const urlObj = new URL(actionUrl);
                // Check if this is our API domain (could be different subdomain/port)
                const isApiUrl = urlObj.pathname.startsWith('/api/v1/');

                if (isApiUrl) {
                  // Extract just the path for our API - this ensures auth headers are sent
                  downloadPath = urlObj.pathname + urlObj.search;
                  // Remove /api/v1 prefix if present, as axios baseURL will add it
                  downloadPath = downloadPath.replace(/^\/api\/v1/, '');
                }
                // else: external URL — use downloadPath as-is
              } catch {
                // Not a valid URL, assume it's already a relative path
                // Remove /api/v1 prefix if present
                downloadPath = downloadPath.replace(/^\/api\/v1/, '');
              }

              // Use authenticated API client to fetch the file
              const response = await apiRequestWithHeaders<Blob>({
                method: "GET",
                url: downloadPath,
                responseType: "blob",
              });

              const blob = response.data;

              // Priority: 1) metadata.filename, 2) label-based lookup, 3) label with inferred extension
              let filename = action.metadata?.filename;
              let effectiveMimeType = action.metadata?.mime_type;

              if (!filename) {
                // Try to infer from label if no metadata filename
                const labelFallback = LABEL_FILE_TYPE_MAP[action.label];

                if (labelFallback) {
                  // Use label as base filename with correct extension from map
                  filename =
                    action.label.replace(/\s+/g, "_") + labelFallback.extension;
                  effectiveMimeType = effectiveMimeType || labelFallback.mimeType;
                } else {
                  // Unknown label - use label as filename and determine extension from mime_type or default
                  filename = action.label || "document";

                  if (!filename.includes(".")) {
                    if (effectiveMimeType?.includes("pdf")) {
                      filename = `${filename}.pdf`;
                    } else if (
                      effectiveMimeType?.includes("spreadsheetml") ||
                      effectiveMimeType?.includes("excel")
                    ) {
                      filename = `${filename}.xlsx`;
                    } else if (effectiveMimeType?.includes("csv")) {
                      filename = `${filename}.csv`;
                    } else if (
                      effectiveMimeType?.includes("presentationml") ||
                      effectiveMimeType?.includes("powerpoint")
                    ) {
                      filename = `${filename}.pptx`;
                    } else if (
                      effectiveMimeType?.includes("wordprocessingml") ||
                      effectiveMimeType?.includes("msword")
                    ) {
                      filename = `${filename}.docx`;
                    } else {
                      filename = `${filename}.pdf`; // final fallback to PDF
                    }
                  }
                }
              }

              const downloadUrl = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = downloadUrl;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(downloadUrl);
              document.body.removeChild(a);
            } catch (error) {
              console.error("Error downloading file:", error);
            } finally {
              setDownloadingActionId(null);
            }
          };

          // Determine effective mime type for icon selection (metadata or label-based fallback)
          const labelFallbackForIcon = LABEL_FILE_TYPE_MAP[action.label];
          const effectiveMimeTypeForIcon =
            action.metadata?.mime_type || labelFallbackForIcon?.mimeType;
          const effectiveFilenameForIcon = action.metadata?.filename;

          const { icon: fileIcon, color: iconColor } = getFileIconAndColor(
            effectiveMimeTypeForIcon,
            effectiveFilenameForIcon,
          );

          return (
            <div
              key={`download-${index}`}
              className="inline-block border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800"
            >
              <button
                type="button"
                onClick={handleDirectDownload}
                className="flex cursor-pointer items-center hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-200 disabled:cursor-not-allowed"
                disabled={downloadingActionId === actionId}
              >
                <div className="p-2 h-full flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={fileIcon}
                    className={`text-lg ${iconColor}`}
                  />
                </div>
                <div className="p-2 px-3 flex-grow truncate flex min-w-0">
                  <span className="font-medium text-sm dark:text-gray-200 truncate">
                    {action.label}
                  </span>
                </div>
                <div className="p-2 px-3">
                  {downloadingActionId === actionId ? (
                    <Spinner size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={faDownload} className="text-sm" />
                  )}
                </div>
              </button>
            </div>
          );
        });

      const actionButtons = [...viewActionButtons, ...downloadActionButtons];

      // Return content, attachments, and action buttons
      return (
        <>
          {textContentNode}
          {embedsNode}
          {attachments && attachments}
          {sourcesDisplay && sourcesDisplay}
          {actionButtons.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">{actionButtons}</div>
          )}
        </>
      );
    }

    // Default rendering with content and attachments
    return (
      <>
        {textContentNode}
        {embedsNode}
        {attachments}
        {sourcesDisplay}
      </>
    );
  };

  if (state.loading) {
    return <div className="p-4 flex h-full w-full text-slate-700 dark:text-slate-300">Loading messages...</div>;
  }

  return (
    <TaskThreadDrawerProvider>
    <EntityPreviewCardFocusProvider>
    <div className="flex flex-col h-full  w-full rounded-lg">
      {/* Error message at the top if there is an error */}
      {state.error && (
        <div className="p-4">
          <ErrorBanner message={`Error loading chat history: ${state.error}`} />
        </div>
      )}

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        className={`flex-1 overflow-x-hidden overflow-y-auto px-6 pt-6 flex flex-col-reverse text-slate-900 dark:text-white ${isMinimal && isAni ? "pb-2" : "pb-4"}`}
        style={{
          maskImage: `linear-gradient(to bottom,
            transparent 0px,
            black 30px,
            black calc(100% - 30px),
            transparent 100%
          )`,
          WebkitMaskImage: `linear-gradient(to bottom,
            transparent 0px,
            black 30px,
            black calc(100% - 30px),
            transparent 100%
          )`,
        }}
      >
        <div className="space-y-4 ">
          {isAni && displayMode === "developer" && richContext && contextLabel && (
            <AniDeveloperMenu
              richContext={richContext}
              contextLabel={contextLabel}
              showMcpDebugPanel={showMcpDebugPanel}
              showFeToolDebugPanel={showFeToolDebugPanel}
              feToolDebugRunner={feToolDebugRunner}
              feToolDebugOsContext={feToolDebugOsContext}
              feOpenAppAllowlist={feOpenAppAllowlist}
              agentPayloadCurrentApp={richContext.osContext?.focusedApp}
              agentPayloadPersonalityMode={aniPersonalityMode}
              agentPayloadCompanyId={effectiveAgentPayloadCompanyId}
            />
          )}

          {/* Hide chat interface in developer mode */}
          {displayMode !== "developer" && (state.messages.length === 0 ? (
            <>
              <div className="text-center py-8 text-slate-600 dark:text-gray-500">
                {state.error ? (
                  "Couldn't load previous messages."
                ) : (
                  <div className="flex flex-col items-center gap-6">
                    {customAvatar ? (
                      <div className="size-12 flex items-center justify-center">
                        {customAvatar}
                      </div>
                    ) : (
                      <Logo iconOnly className="size-12" />
                    )}
                    <div>
                      <p className="text-xl font-semibold text-slate-900 dark:text-slate-200">
                        Hi, I&apos;m {isAni ? "Ani" : recipientName}...
                      </p>
                      <p className="text-slate-600 dark:text-gray-400">
                        How can I help you today?
                      </p>
                    </div>
                    {showSamplePrompts && (
                      <div className="flex flex-wrap justify-center gap-2 px-2">
                        {samplePrompts!.map((prompt) => (
                          <button
                            key={prompt}
                            type="button"
                            className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                            onClick={() => void submitOutboundMessage(prompt)}
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            // With flex-col-reverse, the latest messages will naturally appear at the bottom
            (() => {
              const filteredMessages = state.messages.filter((message) => {
                // Hide initial assistant message when showing context card or example chat
                if (
                  isAni &&
                  message.id === "initial-assistant-message"
                ) {
                  return false;
                }

                // Hide empty persisted messages that have no renderable payload.
                // This can happen for orchestrator continuation turns with blank content.
                const hasRenderableContent =
                  typeof message.content === "string" &&
                  message.content.trim().length > 0;
                const hasRenderableAttachments =
                  Array.isArray(message.attachments) &&
                  message.attachments.length > 0;
                const hasRenderableActions =
                  Array.isArray(message.actions) && message.actions.length > 0;
                const hasRenderableSources =
                  Array.isArray(message.sources) && message.sources.length > 0;
                const hasRenderableEmbeds =
                  parseChatEmbeds(message.metadata?.embeds).length > 0;
                const hasRenderableToolStep =
                  parseToolStep(message.metadata?.tool_step) !== null;
                const hasRenderableTimeline =
                  Array.isArray(message.metadata?.timeline) &&
                  message.metadata.timeline.length > 0;
                const hasRenderableThinking = message.metadata?.thinking === true;

                if (
                  !hasRenderableContent &&
                  !hasRenderableAttachments &&
                  !hasRenderableActions &&
                  !hasRenderableSources &&
                  !hasRenderableEmbeds &&
                  !hasRenderableToolStep &&
                  !hasRenderableTimeline &&
                  !hasRenderableThinking
                ) {
                  return false;
                }

                return true;
              });

              let lastAssistantCopyMessageId: string | null = null;
              if (enableAssistantMarkdown) {
                for (let i = filteredMessages.length - 1; i >= 0; i -= 1) {
                  const candidate = filteredMessages[i];
                  if (candidate.sender.type === "user") {
                    continue;
                  }
                  const markdown = assistantMarkdownForCopy(candidate);
                  if (markdown.length > 0) {
                    lastAssistantCopyMessageId = candidate.id;
                    break;
                  }
                }
              }

              return filteredMessages.map((message, index) => {
                const previousMessage =
                  index > 0 ? filteredMessages[index - 1] : undefined;
                const showDayBreak = shouldShowDayBreak(
                  message,
                  previousMessage,
                  state.messages,
                );

                const fullListIndex = state.messages.indexOf(message);
                const rowKey = stableMessageRowKey(
                  message,
                  fullListIndex >= 0 ? fullListIndex : index,
                );

                const normMessageId = normalizeMessageId(message.id);
                const isNewMessage =
                  liveLineRevealMessageId !== null &&
                  normMessageId === liveLineRevealMessageId &&
                  !initialMessageIds.current.has(normMessageId) &&
                  message.sender.type !== "user";

                const isToolStepMessage =
                  parseToolStep(message.metadata?.tool_step) !== null;
                const previousIsToolStep =
                  previousMessage !== undefined &&
                  parseToolStep(previousMessage.metadata?.tool_step) !== null;
                const previousIsAssistant =
                  previousMessage !== undefined &&
                  previousMessage.sender.type !== "user" &&
                  !parseToolStep(previousMessage.metadata?.tool_step);
                const minimalAssistantTurnBreak =
                  isMinimal &&
                  isAni &&
                  message.sender.type !== "user" &&
                  !isToolStepMessage &&
                  (previousIsAssistant || previousIsToolStep);

                const assistantCopyMarkdown =
                  enableAssistantMarkdown && message.sender.type !== "user"
                    ? assistantMarkdownForCopy(message)
                    : "";
                const showAssistantCopyButton =
                  assistantCopyMarkdown.length > 0 &&
                  message.id === lastAssistantCopyMessageId;

                if (isToolStepMessage) {
                  return (
                    <React.Fragment key={rowKey}>
                      <div
                        data-message-id={rowKey}
                        className={
                          isMinimal && isAni ? (compactTimeline ? "mb-3" : "mb-3 pl-[37px]") : "pl-[37px] -mt-2"
                        }
                      >
                        {renderMessageContent(message, isNewMessage)}
                      </div>
                    </React.Fragment>
                  );
                }

                return (
                  <React.Fragment key={rowKey}>
                    {showDayBreak && (
                      <ChatDayBreak
                        date={new Date(message.timestamp)}
                        onJumpToDate={(targetDate) => {
                          // Find the message closest to the target date
                          const targetTime = targetDate.getTime();
                          let closestMessage = state.messages[0]; // Default to first message
                          let smallestDiff = Infinity;

                          for (const message of state.messages) {
                            const diff = Math.abs(message.timestamp - targetTime);
                            if (diff < smallestDiff) {
                              smallestDiff = diff;
                              closestMessage = message;
                            }
                          }

                          // Use setTimeout to ensure DOM is updated
                          setTimeout(() => {
                            let messageElement = null;

                            // Method 1: Match DOM `data-message-id` (same as list row key)
                            const closestIdx = state.messages.indexOf(
                              closestMessage,
                            );
                            const scrollTargetId = stableMessageRowKey(
                              closestMessage,
                              closestIdx >= 0 ? closestIdx : 0,
                            );
                            if (
                              scrollTargetId &&
                              scrollTargetId !== "undefined" &&
                              scrollTargetId !== "null"
                            ) {
                              messageElement = document.querySelector(
                                `[data-message-id="${scrollTargetId}"]`,
                              );
                            }

                            // Method 2: Find by timestamp proximity using all message elements
                            if (!messageElement) {
                              const allMessageElements =
                                document.querySelectorAll("[data-message-id]");
                              let closestElement = null;
                              let closestTimestampDiff = Infinity;

                              for (const element of allMessageElements) {
                                const messageId =
                                  element.getAttribute("data-message-id");
                                if (
                                  messageId &&
                                  messageId !== "undefined" &&
                                  messageId !== "null"
                                ) {
                                  const messageWithId = state.messages.find(
                                    (m, mi) =>
                                      stableMessageRowKey(m, mi) === messageId,
                                  );
                                  if (messageWithId) {
                                    const diff = Math.abs(
                                      messageWithId.timestamp - targetTime,
                                    );
                                    if (diff < closestTimestampDiff) {
                                      closestTimestampDiff = diff;
                                      closestElement = element;
                                    }
                                  }
                                }
                              }

                              if (closestElement) {
                                messageElement = closestElement;
                              }
                            }

                            // Method 3: Find by timestamp proximity using all message divs (more generic)
                            if (!messageElement) {
                              const allMessageDivs = document.querySelectorAll(
                                ".flex.flex-col.justify-start.items-start",
                              );
                              let closestElement = null;
                              let closestTimestampDiff = Infinity;

                              for (const element of allMessageDivs) {
                                const messageId =
                                  element.getAttribute("data-message-id");
                                if (
                                  messageId &&
                                  messageId !== "undefined" &&
                                  messageId !== "null"
                                ) {
                                  const messageWithId = state.messages.find(
                                    (m, mi) =>
                                      stableMessageRowKey(m, mi) === messageId,
                                  );
                                  if (messageWithId) {
                                    const diff = Math.abs(
                                      messageWithId.timestamp - targetTime,
                                    );
                                    if (diff < closestTimestampDiff) {
                                      closestTimestampDiff = diff;
                                      closestElement = element;
                                    }
                                  }
                                }
                              }

                              if (closestElement) {
                                messageElement = closestElement;
                              }
                            }

                            if (messageElement) {
                              messageElement.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            } else {
                              // Method 4: Final fallback - scroll to approximate position based on message index

                              const messagesContainer = document.querySelector(
                                ".flex-1.overflow-y-auto",
                              );
                              if (messagesContainer) {
                                const messageIndex =
                                  state.messages.indexOf(closestMessage);
                                const totalMessages = state.messages.length;

                                if (messageIndex >= 0 && totalMessages > 0) {
                                  const scrollPercentage =
                                    messageIndex / totalMessages;
                                  const scrollTop =
                                    scrollPercentage *
                                    messagesContainer.scrollHeight;

                                  messagesContainer.scrollTo({
                                    top: scrollTop,
                                    behavior: "smooth",
                                  });
                                }
                                // No fallback needed - invalid message index
                              }
                              // No fallback needed - messages container not found
                            }
                          }, 100);
                        }}
                      />
                    )}
                    <motion.div
                      className={`flex w-full flex-col justify-start items-start${
                        minimalAssistantTurnBreak ? ` ${ANI_MINIMAL_ASSISTANT_TURN_BREAK_CLASS}` : ""
                      }${showAssistantCopyButton ? " group/copy" : ""}`}
                      data-message-id={rowKey}
                      data-sender-type={message.sender.type}
                      initial={isNewMessage ? { opacity: 0 } : false}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    >
                      <div className="flex w-full min-w-0 max-w-full items-start gap-3">
                        {!isMinimal && (
                          <>
                            {message.sender.type === "worker" ? (
                              <div style={{ minWidth: "26px" }}>
                                {message.sender.name === "Ani" ? (
                                  <Logo iconOnly className="h-8 w-8" />
                                ) : (
                                  <WorkerAvatar
                                    seed={message.sender.name}
                                    image={message.sender.image}
                                    size={26}
                                  />
                                )}
                              </div>
                            ) : (
                              <WorkerAvatar
                                seed={
                                  message.sender.type === "user"
                                    ? userAvatarSeed(user)
                                    : message.sender.name
                                }
                                image={message.sender.image}
                                size={26}
                                user={message.sender.type === "user"}
                              />
                            )}
                          </>
                        )}
                        <div className={`${isMinimal ? "mt-0 gap-0.5" : "mt-1 gap-2"} flex w-full min-w-0 max-w-full flex-col`}>
                          {!isMinimal && (
                            <div className="flex items-center leading-none gap-2">
                              <div className="font-medium text-base text-slate-900 dark:text-slate-100">
                                {message.sender.type === "user" &&
                                user?.id === message.sender.id &&
                                message.sender.name !== "You"
                                  ? `${message.sender.name} (You)`
                                  : message.sender.name}
                              </div>
                              <div className="text-sm text-slate-400 dark:text-slate-500 font-mono top-[1px] relative">
                                {formatTime(message.timestamp)}
                              </div>
                            </div>
                          )}

                          <div className="relative w-full min-w-0">
                            <div
                              className={`text-sm font-light pl-0 break-words [overflow-wrap:anywhere] min-w-0 max-w-full ${isMinimal ? "leading-relaxed" : "leading-relaxed"} ${isMinimal && message.sender.type === "user"
                                ? "relative -left-2 w-full rounded-lg bg-slate-200 !px-3 py-2 text-slate-900 dark:bg-slate-800/80 dark:text-white"
                                : "text-slate-800 dark:text-slate-300"
                                }`}
                              data-testid="chat-message-content"
                            >
                              {renderMessageContent(message, isNewMessage)}
                            </div>
                            {showAssistantCopyButton ? (
                              <div
                                className="pointer-events-none absolute bottom-0 end-0 z-10 overflow-visible opacity-0 transition-opacity duration-200 ease-out group-hover/copy:pointer-events-auto group-hover/copy:opacity-100"
                              >
                                <ChatAssistantCopyButton
                                  text={assistantCopyMarkdown}
                                  className={CHAT_ASSISTANT_COPY_BUTTON_POSITION_CLASS}
                                />
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </React.Fragment>
                );
              });
            })()
          ))}
          {/* Typing indicator */}

          {displayMode !== "developer" && state.isTyping && !liveTurnPlaceholderActive && (
            <div className={`${isAni ? "" : "ml-[34px] "}mt-8`}>
              <LoadingIndicator dotCount={3} dotSize={4} dotSpacing={3} />
            </div>
          )}

          {showSamplePrompts && state.messages.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 px-2 mt-4">
              {samplePrompts!.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                  onClick={() => void submitOutboundMessage(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}

          {/* Removed separate typing container to prevent double rendering */}

          {/* Place messagesEndRef here, after the messages and typing indicator */}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* System Tasks Banner */}
      {/* {fetchSystemTasks && recipientId && (
        <div className="px-4 pt-4">
          <SystemTasks
            projectId={recipientId}
            systemTaskChannel={systemTaskChannel}
            fetchSystemTasks={fetchSystemTasks}
          />
        </div>
      )} */}

      {/* Active System Jobs Display */}
      <AnimatePresence>
        {(activeSystemJobs.size > 0 || completedJobsToShow.size > 0) && (
          <motion.div
            className="mx-4 mb-2"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="space-y-1.5 font-mono text-sm">
              {/* Active jobs */}
              {Array.from(activeSystemJobs.values()).map((job, idx) => (
                <motion.div
                  key={String(job._id ?? job.id ?? `active-${idx}`)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors bg-slate-100 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                >
                  <LoadingIndicator dotCount={3} dotSize={3} dotSpacing={2} />
                  <span className="text-sm flex-1 text-slate-700 dark:text-slate-400">
                    {job.description ?? ""}
                  </span>
                  <SystemJobTimer createdAt={job.created_at ?? Date.now()} />
                </motion.div>
              ))}
              {/* Completed jobs */}
              {Array.from(completedJobsToShow.values()).map((job, idx) => (
                <motion.div
                  key={String(job._id ?? job.id ?? `done-${idx}`)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors bg-green-100 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0, scale: [1, 1.05, 1] }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                >
                  <FontAwesomeIcon
                    icon={faSparkles}
                    className="text-green-600 dark:text-green-400"
                  />
                  <span className="text-sm flex-1 text-green-700 dark:text-green-300">
                    {(job.description ?? "").startsWith("✅")
                      ? job.description ?? ""
                      : `✅ ${(job.description ?? "").replace(/\.{3}$/, "")}`}
                  </span>
                  <SystemJobTimer createdAt={job.created_at ?? Date.now()} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <OutboundMessageQueuePanel
        queue={outboundQueue}
        agentLabel={isAni ? "Ani" : recipientName}
        onRemove={removeFromQueue}
      />

      {/* Chat Input - hidden in developer mode */}
      {displayMode !== "developer" && (
        <ChatInput
          recipientId={recipientId}
          recipientName={recipientName}
          conversationId={conversationId}
          conversationTitle={conversationTitle}
          onSendMessage={submitOutboundMessage}
          disabled={isComposerDisabled}
          placeholder={followUpPlaceholder}
          disableSpeechRecognition={true}
          miniUpload={miniUpload}
          onCreateConversation={onCreateConversation}
          useAgenticV2Documents={useAgenticV2Documents}
          inputContainerClassName={inputContainerClassName}
          aniPersonalityMode={
            isAni && isMinimal ? aniPersonalityMode : undefined
          }
          onAniPersonalityModeChange={
            isAni && isMinimal ? onAniPersonalityModeChange : undefined
          }
        />
      )}
    </div>
    </EntityPreviewCardFocusProvider>
    </TaskThreadDrawerProvider>
  );
}
